import './style.css';
import { supabase, getSession, signOut } from './supabase.js';
import { showAuthShell } from './auth.js';
import { showLanding, teardownLanding } from './landing.js';
import { showLegal } from './legal.js';
import { db } from './db.js';
import { streamGeneration } from './api.js';
import { startCheckout, openBillingPortal } from './stripe.js';
import { PLAN_LIMITS, planLimit, remaining, canGenerate, usageLabel, PLAN_LABELS } from './usage.js';

/* ============== CONSTANTS ============== */
const TYPES = {
  blog:{label:'Blog Post',words:1200,price:250,prompt:'a comprehensive blog post with H2/H3 headings, an engaging intro, well-structured body, and a strong conclusion'},
  social:{label:'Social Pack',words:400,price:120,prompt:'a pack of 5 social media posts (LinkedIn, X/Twitter, Instagram, Facebook, Threads) — clearly labeled, each tailored to platform tone and length'},
  email:{label:'Email Sequence',words:900,price:350,prompt:'a 5-email nurture sequence with subject lines, preheaders, and clear CTAs — labeled Email 1 through Email 5'},
  ad:{label:'Ad Copy',words:300,price:150,prompt:'5 ad copy variations (Google/Meta-friendly): each with a headline, primary text (90 chars), and description (30 chars)'},
  video:{label:'Video Script',words:800,price:300,prompt:'a video script with HOOK, INTRO, BODY (with timestamps), and CTA — written for spoken delivery'},
  newsletter:{label:'Newsletter',words:700,price:180,prompt:'a newsletter issue with a punchy subject line, opening note, 3 curated sections with takeaways, and a sign-off CTA'},
  landing:{label:'Landing Page',words:900,price:400,prompt:'full landing page copy with: hero headline + subhead, 3 benefit bullets, 3 feature blocks, social proof line, FAQ (3 Qs), and final CTA'},
  thread:{label:'Twitter Thread',words:500,price:140,prompt:'an X/Twitter thread of 8-12 numbered tweets, each <280 chars, with a strong hook tweet and CTA tweet at the end'}
};
const TONES = ['Professional','Friendly','Authoritative','Conversational','Witty','Bold','Empathetic','Technical','Inspirational','Luxury'];
const LENGTHS = {short:'Short (~50% of standard)',standard:'Standard',long:'Long (~150% of standard)'};
const READ_LEVELS = ['Grade 6 (very accessible)','Grade 8 (accessible)','Grade 10 (general adult)','Grade 12 (educated adult)','College (industry-savvy)'];
const VIEWS = ['dashboard','generate','clients','bulk','library','outreachhub','invoices','plans','pricing','account'];

const NICHES = [
  'Shopify Brand','Real Estate Agent','Med Spa','Restaurant',
  'Gym/Fitness','SaaS Startup','Mortgage Broker','Custom…'
];
const HUB_PLATFORMS = ['Google','Instagram','LinkedIn','Twitter','TikTok'];
const HUB_MSG_TYPES = {
  cold_email: { label:'Cold Email',  kind:'email',    icon:'✉' },
  dm:         { label:'Instagram DM',kind:'dm',       icon:'📷' },
  linkedin:   { label:'LinkedIn',    kind:'linkedin', icon:'💼' },
  twitter_dm: { label:'Twitter / X', kind:'twitter',  icon:'𝕏' },
  tiktok_dm:  { label:'TikTok DM',   kind:'tiktok',   icon:'♪' }
};
const REPLY_STATUSES = {
  sent:           { label:'Sent',           cls:'r-sent' },
  replied:        { label:'Replied',        cls:'r-rep'  },
  interested:     { label:'Interested',     cls:'r-int'  },
  not_interested: { label:'Not Interested', cls:'r-no'   },
  converted:      { label:'Converted',      cls:'r-conv' }
};
const TEMPLATE_TYPES = {
  cold:      'Cold',
  follow_up: 'Follow-up',
  referral:  'Referral Ask'
};
const FOLLOWUP_DAYS_OPTIONS = [3, 5, 7];

const PRICE_STARTER = import.meta.env.VITE_PRICE_STARTER;
const PRICE_PRO = import.meta.env.VITE_PRICE_PRO;

/* ============== LOCALSTORAGE MIGRATION ============== */
// One-time, safe rename of legacy `quill.*` localStorage keys to `managen.*`.
// Copies each old key to its new name (without clobbering an existing new key),
// then removes the old key. Runs before state initialization reads any key.
(function migrateLocalStorageKeys() {
  try {
    const legacy = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('quill.')) legacy.push(k);
    }
    for (const oldKey of legacy) {
      const newKey = 'managen.' + oldKey.slice('quill.'.length);
      if (localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, localStorage.getItem(oldKey));
      }
      localStorage.removeItem(oldKey);
    }
  } catch (e) {
    // localStorage may be unavailable (private mode / blocked) — migration is best-effort.
    console.warn('[managen] localStorage migration skipped', e);
  }
})();

/* ============== STATE ============== */
const state = {
  user: null,
  profile: null,
  usage: 0,
  clients: [],
  library: [],
  invoices: [],
  contacts: [],
  activity: [],
  prospects: [],
  queue: [],
  templates: [],
  hubFollowupDays: parseInt(localStorage.getItem('managen.followupDays') || '5'),
  current: 'dashboard',
  streamCtl: null,
  bulkActive: false
};

// Inline onchange="..." handlers in templates run in global scope, so expose `state`.
window.state = state;

/* ============== HELPERS ============== */
function uid(){return Math.random().toString(36).slice(2,9)+Date.now().toString(36).slice(-4)}
function newUuid(){return crypto.randomUUID ? crypto.randomUUID() : uid()+'-'+uid()}
function fmtDate(ts){const d=new Date(ts);return d.toLocaleDateString(undefined,{month:'short',day:'numeric',year:'numeric'})+' '+d.toLocaleTimeString(undefined,{hour:'2-digit',minute:'2-digit'})}
function fmtMoney(n){return '$'+(Number(n)||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
function escapeHtml(s){return String(s??'').replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]))}
function slugify(s){return String(s||'').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'').slice(0,60)||'doc'}

async function logActivity(kind, text) {
  const entry = { id: newUuid(), kind, text, ts: Date.now() };
  state.activity.unshift(entry);
  state.activity = state.activity.slice(0, 50);
  try { await db.insertActivity(kind, text); } catch (e) { console.warn('activity log failed', e); }
}

/* ============== TOP-LEVEL APP SHELL ============== */
function mountAppShell() {
  document.getElementById('root').innerHTML = `
    <div class="app" id="app">
      <aside class="sidebar">
        <div class="brand">
          <div class="logo"></div>
          <div>
            <div class="name">MANA<span class="ll">GEN</span></div>
            <div class="tag">Content Studio</div>
          </div>
        </div>
        <nav class="nav" id="nav">
          <button data-view="dashboard" class="active">Dashboard</button>
          <button data-view="generate">Generate</button>
          <button data-view="clients">Clients</button>
          <button data-view="bulk">Bulk Run</button>
          <button data-view="library">Library</button>
          <button data-view="outreachhub">Outreach Hub</button>
          <button data-view="invoices">Invoices</button>
          <button data-view="plans">Plans</button>
          <button data-view="pricing">Rate Calc</button>
          <button data-view="account">Account</button>
        </nav>
        <div class="foot">
          <span id="planState">—</span>
          <button onclick="window.__signOut && window.__signOut()">Sign out</button>
        </div>
      </aside>
      <section class="main">
        <header class="topbar">
          <div class="crumbs"><b id="crumbView">Dashboard</b></div>
          <div class="right">
            <span class="pill" id="usagePill">—</span>
            <span class="pill" id="emailPill">—</span>
          </div>
        </header>
        <div class="view" id="view"></div>
      </section>
    </div>
  `;

  document.getElementById('nav').addEventListener('click', e => {
    const b = e.target.closest('button[data-view]'); if (!b) return; go(b.dataset.view);
  });
  window.__signOut = async () => { await signOut(); window.location.assign('/'); };
}

/* ============== ROUTING ============== */
// The app lives under /app/<view>. Landing lives at /.
function appViewFromPath() {
  const m = window.location.pathname.match(/^\/app\/([a-z]+)/i);
  const v = m && m[1] ? m[1].toLowerCase() : 'dashboard';
  return VIEWS.includes(v) ? v : 'dashboard';
}

function go(v, opts = {}) {
  if (!VIEWS.includes(v)) v = 'dashboard';
  state.current = v;
  const path = '/app/' + v;
  if (window.location.pathname !== path) {
    history[opts.replace ? 'replaceState' : 'pushState']({ view: v }, '', path);
  }
  document.querySelectorAll('#nav button').forEach(b => b.classList.toggle('active', b.dataset.view === v));
  document.getElementById('crumbView').textContent = v.charAt(0).toUpperCase() + v.slice(1);
  render();
}
window.__go = go;

/* ============== RENDER ============== */
function render() {
  updateTopbar();
  const el = document.getElementById('view');
  const v = state.current;
  if (v === 'dashboard') el.innerHTML = viewDashboard();
  else if (v === 'generate') el.innerHTML = viewGenerate();
  else if (v === 'clients') el.innerHTML = viewClients();
  else if (v === 'bulk') el.innerHTML = viewBulk();
  else if (v === 'library') el.innerHTML = viewLibrary();
  else if (v === 'outreachhub') el.innerHTML = viewOutreachHub();
  else if (v === 'invoices') el.innerHTML = viewInvoices();
  else if (v === 'plans') el.innerHTML = viewPlans();
  else if (v === 'pricing') el.innerHTML = viewPricing();
  else if (v === 'account') el.innerHTML = viewAccount();

  if (v === 'generate') hookGenerate();
  if (v === 'bulk') hookBulk();
  if (v === 'outreachhub') hookOutreachHub();
  if (v === 'invoices') hookInvoices();
  if (v === 'pricing') hookPricing();
  if (v === 'plans') hookPlans();
  if (v === 'account') hookAccount();
}

function updateTopbar() {
  const usagePill = document.getElementById('usagePill');
  const emailPill = document.getElementById('emailPill');
  const planState = document.getElementById('planState');
  if (!usagePill) return;
  const plan = state.profile?.plan || 'trial';
  const lbl = usageLabel(plan, state.usage);
  usagePill.textContent = lbl;
  usagePill.classList.toggle('warn', plan === 'trial' && remaining(plan, state.usage) <= 2);
  usagePill.classList.toggle('bad', !canGenerate(plan, state.usage));
  emailPill.textContent = state.user?.email || '—';
  if (planState) planState.textContent = (PLAN_LABELS[plan] || plan).toUpperCase();
}

/* ============== DASHBOARD ============== */
function viewDashboard() {
  const totalPieces = state.library.length;
  const totalWords = state.library.reduce((a,b)=>a + (b.wordCount||0), 0);
  const value = state.library.reduce((a,b)=>a + (TYPES[b.type]?.price||0), 0);
  const invoiced = state.invoices.reduce((a,b)=>a + (b.total||0), 0);
  const sentLike = state.queue.filter(q => ['sent','replied','interested','not_interested','converted'].includes(q.status));
  const replied = state.queue.filter(q => ['replied','interested','converted'].includes(q.status)).length;
  const converted = state.queue.filter(q => q.status === 'converted').length;
  const conversionRate = sentLike.length ? Math.round((converted / sentLike.length) * 100) : 0;
  return `
  <div class="grid cards" style="margin-bottom:16px">
    <div class="card kpi"><div class="label">Total Pieces</div><div class="value">${totalPieces}</div><div class="sub">${state.library.filter(x=>Date.now()-x.ts<7*864e5).length} this week</div></div>
    <div class="card kpi"><div class="label">Words Generated</div><div class="value">${totalWords.toLocaleString()}</div><div class="sub">${state.library.length?Math.round(totalWords/state.library.length):0} avg / piece</div></div>
    <div class="card kpi"><div class="label">Saved Items</div><div class="value">${state.library.length}</div><div class="sub">${state.clients.length} active clients</div></div>
    <div class="card kpi"><div class="label">Prospects Found</div><div class="value">${state.prospects.length}</div><div class="sub">${state.queue.filter(q=>q.status==='approved').length} ready to send</div></div>
    <div class="card kpi"><div class="label">Messages Sent</div><div class="value">${sentLike.length}</div><div class="sub">${state.queue.filter(q=>q.sentAt&&Date.now()-q.sentAt<7*864e5).length} this week</div></div>
    <div class="card kpi"><div class="label">Replies Received</div><div class="value">${replied}</div><div class="sub">${converted} converted</div></div>
    <div class="card kpi"><div class="label">Conversion Rate</div><div class="value">${conversionRate}%</div><div class="sub">converted / sent</div></div>
    <div class="card kpi"><div class="label">Est. Content Value</div><div class="value">${fmtMoney(value)}</div><div class="sub">at standard agency rates</div></div>
    <div class="card kpi"><div class="label">Total Invoiced</div><div class="value">${fmtMoney(invoiced)}</div><div class="sub">${state.invoices.length} invoices issued</div></div>
  </div>
  <div class="grid" style="grid-template-columns:1fr 1fr;gap:16px">
    <div class="card">
      <h3>Quick Create</h3>
      <div class="row" style="gap:8px;flex-wrap:wrap;margin-top:8px">
        ${Object.entries(TYPES).map(([k,t])=>`<button class="btn sm" onclick="window.__quick('${k}')">${t.label}</button>`).join('')}
      </div>
    </div>
    <div class="card">
      <h3>Recent Activity</h3>
      ${state.activity.length===0?'<div class="empty" style="padding:18px">No activity yet — generate something.</div>':
      `<div class="activity">${state.activity.slice(0,8).map(a=>`
        <div class="e"><div class="t">${fmtDate(a.ts).split(' ').slice(0,2).join(' ')}</div>
        <div>${escapeHtml(a.text)}</div>
        <div class="tag">${escapeHtml(a.kind)}</div></div>`).join('')}</div>`}
    </div>
  </div>`;
}
window.__quick = (type) => { state._quickType = type; go('generate'); };

/* ============== GENERATE ============== */
function viewGenerate() {
  const presetType = state._quickType || 'blog';
  state._quickType = null;
  const blocked = !canGenerate(state.profile?.plan || 'trial', state.usage);
  return `
  ${blocked ? `<div class="card" style="border-color:var(--bad);margin-bottom:14px">
    <h3 style="color:var(--bad);margin-bottom:6px">Generation limit reached</h3>
    <div style="font-size:13px;color:var(--ink-dim);margin-bottom:10px">You've used your plan's allowance for this period. Upgrade to keep generating.</div>
    <button class="btn primary" onclick="window.__go('plans')">View Plans</button>
  </div>`:''}
  <div class="split">
    <div class="card">
      <h2>New Brief</h2>
      <div class="field">
        <label>Client (optional — auto-fills)</label>
        <select class="select" id="g_client" onchange="window.__applyClientToBrief()">
          <option value="">— None —</option>
          ${state.clients.map(c=>`<option value="${c.id}">${escapeHtml(c.name)} — ${escapeHtml(c.industry||'')}</option>`).join('')}
        </select>
      </div>
      <div class="field">
        <label>Content Type</label>
        <select class="select" id="g_type">
          ${Object.entries(TYPES).map(([k,t])=>`<option value="${k}" ${k===presetType?'selected':''}>${t.label}</option>`).join('')}
        </select>
      </div>
      <div class="field">
        <label>Topic / Subject</label>
        <input class="input" id="g_topic" placeholder="e.g. how solo founders should approach SEO in 2026">
      </div>
      <div class="field-row">
        <div class="field"><label>Brand Name</label><input class="input" id="g_brand" placeholder="Acme Co"></div>
        <div class="field"><label>Audience</label><input class="input" id="g_audience" placeholder="early-stage SaaS founders"></div>
      </div>
      <div class="field">
        <label>Key Points (one per line)</label>
        <textarea class="textarea" id="g_points" placeholder="- focus on intent over volume&#10;- AI search changes everything"></textarea>
      </div>
      <div class="field-row">
        <div class="field"><label>Tone</label>
          <select class="select" id="g_tone">${TONES.map(t=>`<option>${t}</option>`).join('')}</select>
        </div>
        <div class="field"><label>Length</label>
          <select class="select" id="g_length">${Object.entries(LENGTHS).map(([k,v])=>`<option value="${k}" ${k==='standard'?'selected':''}>${v}</option>`).join('')}</select>
        </div>
        <div class="field"><label>Reading Level</label>
          <select class="select" id="g_read">${READ_LEVELS.map((r,i)=>`<option ${i===2?'selected':''}>${r}</option>`).join('')}</select>
        </div>
      </div>
      <div class="field">
        <label>Call to Action</label>
        <input class="input" id="g_cta" placeholder="Book a strategy call">
      </div>
      <div class="row">
        <button class="btn primary" id="g_run" ${blocked?'disabled':''}>Generate</button>
        <button class="btn ghost" id="g_stop" disabled>Stop</button>
      </div>
    </div>
    <div>
      <div class="row between" style="margin-bottom:8px">
        <h2 style="margin:0">Output</h2>
        <div class="row">
          <button class="btn sm" id="g_copy">Copy</button>
          <button class="btn sm" id="g_save" disabled>Save to Library</button>
        </div>
      </div>
      <div class="output" id="g_out"></div>
    </div>
  </div>`;
}
window.__applyClientToBrief = () => {
  const id = document.getElementById('g_client').value;
  const c = state.clients.find(x => x.id === id);
  if (!c) return;
  document.getElementById('g_brand').value = c.name || '';
  document.getElementById('g_audience').value = c.audience || '';
  if (c.tone) {
    const sel = document.getElementById('g_tone');
    [...sel.options].forEach(o => { if (o.value.toLowerCase() === c.tone.toLowerCase()) o.selected = true; });
  }
  if (c.notes) {
    const pts = document.getElementById('g_points');
    if (!pts.value.trim()) pts.value = '- ' + c.notes.split(/\n+/).join('\n- ');
  }
};

function hookGenerate() {
  document.getElementById('g_run')?.addEventListener('click', runGenerate);
  document.getElementById('g_stop')?.addEventListener('click', () => state.streamCtl?.abort());
  document.getElementById('g_copy')?.addEventListener('click', () => {
    const t = document.getElementById('g_out').textContent;
    if (!t) return toast('Nothing to copy', 'bad');
    navigator.clipboard.writeText(t); toast('Copied');
  });
  document.getElementById('g_save')?.addEventListener('click', async () => {
    const text = document.getElementById('g_out').textContent;
    if (!text) return;
    const item = {
      id: newUuid(), ts: Date.now(),
      type: document.getElementById('g_type').value,
      topic: document.getElementById('g_topic').value || '(untitled)',
      clientId: document.getElementById('g_client').value || null,
      content: text,
      wordCount: text.trim().split(/\s+/).length,
      brand: document.getElementById('g_brand').value,
      tone: document.getElementById('g_tone').value
    };
    state.library.unshift(item);
    try { await db.insertJson('library', item); }
    catch (e) { toast('Save failed: ' + e.message, 'bad'); return; }
    logActivity('save', `Saved ${TYPES[item.type].label}: "${item.topic}"`);
    toast('Saved to library', 'ok');
  });
}

function buildPrompt(brief) {
  const t = TYPES[brief.type];
  const lengthMul = brief.length === 'short' ? 0.6 : brief.length === 'long' ? 1.5 : 1;
  const targetWords = Math.round(t.words * lengthMul);
  const points = brief.points ? brief.points : '(none provided — infer best-fit angles)';
  return `You are MANAGEN, an elite content strategist for a premium content studio. Produce ${t.prompt}.

BRIEF:
- Topic: ${brief.topic}
- Brand: ${brief.brand||'(unbranded)'}
- Target audience: ${brief.audience||'general'}
- Tone: ${brief.tone}
- Reading level: ${brief.readingLevel}
- Target length: ~${targetWords} words
- Call to action: ${brief.cta||'(none — end on a strong takeaway)'}

KEY POINTS TO COVER:
${points}

OUTPUT RULES:
- Write the final piece only. No preamble, no "here is", no markdown code fences.
- Match the tone and reading level precisely.
- Use clear structure (headings, sections, numbered tweets, etc.) appropriate to the content type.
- Do not include the brief or notes about your process.

Begin:`;
}

async function runGenerate() {
  if (!canGenerate(state.profile?.plan || 'trial', state.usage)) {
    toast('Plan limit reached — upgrade to continue', 'bad');
    return go('plans');
  }
  const type = document.getElementById('g_type').value;
  const brief = {
    type, topic: document.getElementById('g_topic').value.trim(),
    brand: document.getElementById('g_brand').value.trim(),
    audience: document.getElementById('g_audience').value.trim(),
    points: document.getElementById('g_points').value.trim(),
    tone: document.getElementById('g_tone').value,
    length: document.getElementById('g_length').value,
    readingLevel: document.getElementById('g_read').value,
    cta: document.getElementById('g_cta').value.trim()
  };
  if (!brief.topic) return toast('Topic is required', 'bad');

  const out = document.getElementById('g_out');
  out.textContent = ''; out.classList.add('streaming');
  document.getElementById('g_run').disabled = true;
  document.getElementById('g_stop').disabled = false;
  document.getElementById('g_save').disabled = true;

  const ctl = new AbortController();
  state.streamCtl = ctl;

  try {
    await streamGeneration(
      { prompt: buildPrompt(brief), type, maxTokens: 4096 },
      (chunk) => { out.textContent += chunk; },
      ctl.signal
    );
    document.getElementById('g_save').disabled = false;
    state.usage += 1;
    updateTopbar();
    logActivity('generate', `Generated ${TYPES[type].label}: "${brief.topic}"`);
  } catch (e) {
    if (e.name !== 'AbortError') toast('Error: ' + e.message, 'bad');
    if (e.status === 429) await refreshUsage();
  } finally {
    out.classList.remove('streaming');
    document.getElementById('g_run').disabled = !canGenerate(state.profile?.plan || 'trial', state.usage);
    document.getElementById('g_stop').disabled = true;
  }
}

/* ============== CLIENTS ============== */
function viewClients() {
  return `
  <div class="row between" style="margin-bottom:12px">
    <h2 style="margin:0">Clients</h2>
    <button class="btn primary" onclick="window.__editClient()">+ New Client</button>
  </div>
  ${state.clients.length===0?'<div class="empty">No clients yet. Add one to enable bulk runs and brief auto-fill.</div>':
  `<div class="list">${state.clients.map(c=>`
    <div class="item">
      <div>
        <div class="ttl">${escapeHtml(c.name)} <span class="tag">${escapeHtml(c.industry||'—')}</span></div>
        <div class="meta">
          <span>Voice: ${escapeHtml(c.voice||'—')}</span>
          <span>Audience: ${escapeHtml(c.audience||'—')}</span>
          <span>Tone: ${escapeHtml(c.tone||'—')}</span>
        </div>
      </div>
      <div class="acts">
        <button class="btn sm" onclick="window.__editClient('${c.id}')">Edit</button>
        <button class="btn sm danger" onclick="window.__deleteClient('${c.id}')">Delete</button>
      </div>
    </div>`).join('')}</div>`}`;
}
window.__editClient = (id) => {
  const c = id ? state.clients.find(x => x.id === id)
              : { id: newUuid(), name:'', industry:'', voice:'', audience:'', tone:'Professional', notes:'' };
  if (!c) return;
  openModal(id?'Edit Client':'New Client', `
    <div class="field"><label>Name</label><input class="input" id="c_name" value="${escapeHtml(c.name)}"></div>
    <div class="field-row">
      <div class="field"><label>Industry</label><input class="input" id="c_industry" value="${escapeHtml(c.industry||'')}"></div>
      <div class="field"><label>Tone</label>
        <select class="select" id="c_tone">${TONES.map(t=>`<option ${t===c.tone?'selected':''}>${t}</option>`).join('')}</select>
      </div>
    </div>
    <div class="field"><label>Brand Voice</label><input class="input" id="c_voice" value="${escapeHtml(c.voice||'')}" placeholder="e.g. confident expert, no jargon"></div>
    <div class="field"><label>Target Audience</label><input class="input" id="c_audience" value="${escapeHtml(c.audience||'')}" placeholder="e.g. CTOs at Series B startups"></div>
    <div class="field"><label>Notes / Style Guide</label><textarea class="textarea" id="c_notes">${escapeHtml(c.notes||'')}</textarea></div>
  `, [
    { label:'Cancel', className:'btn ghost', action: closeModal },
    { label:'Save Client', className:'btn primary', action: async () => {
      const data = {
        id: c.id,
        name: document.getElementById('c_name').value.trim(),
        industry: document.getElementById('c_industry').value.trim(),
        tone: document.getElementById('c_tone').value,
        voice: document.getElementById('c_voice').value.trim(),
        audience: document.getElementById('c_audience').value.trim(),
        notes: document.getElementById('c_notes').value.trim()
      };
      if (!data.name) return toast('Name required', 'bad');
      try {
        if (id) {
          Object.assign(state.clients.find(x => x.id === id), data);
          await db.upsertJson('clients', data);
        } else {
          state.clients.push(data);
          await db.insertJson('clients', data);
        }
      } catch (e) { return toast('Save failed: ' + e.message, 'bad'); }
      logActivity('client', (id?'Updated':'Added') + ` client "${data.name}"`);
      closeModal(); render(); toast('Client saved', 'ok');
    }}
  ]);
};
window.__deleteClient = async (id) => {
  if (!confirm('Delete this client?')) return;
  state.clients = state.clients.filter(x => x.id !== id);
  try { await db.deleteRow('clients', id); } catch (e) { return toast('Delete failed: ' + e.message, 'bad'); }
  render(); toast('Client deleted');
};

/* ============== BULK ============== */
function viewBulk() {
  const blocked = !canGenerate(state.profile?.plan || 'trial', state.usage);
  return `
  <h2>Bulk Generation</h2>
  ${blocked?'<div class="empty" style="border-color:var(--bad);color:var(--bad);margin-bottom:12px">Generation limit reached. Upgrade in Plans to continue.</div>':''}
  <div class="card" style="max-width:560px">
    <div class="field">
      <label>Client</label>
      <select class="select" id="b_client">
        <option value="">— Select client —</option>
        ${state.clients.map(c=>`<option value="${c.id}">${escapeHtml(c.name)}</option>`).join('')}
      </select>
    </div>
    <div class="field">
      <label>Content Type</label>
      <select class="select" id="b_type">${Object.entries(TYPES).map(([k,t])=>`<option value="${k}">${t.label}</option>`).join('')}</select>
    </div>
    <div class="field"><label>Topic / Theme</label><input class="input" id="b_topic" placeholder="e.g. weekly LinkedIn posts on B2B AI sales"></div>
    <div class="field-row">
      <div class="field"><label>Quantity (1–10)</label><input class="input" id="b_qty" type="number" min="1" max="10" value="3"></div>
      <div class="field"><label>Tone</label>
        <select class="select" id="b_tone">${TONES.map(t=>`<option>${t}</option>`).join('')}</select>
      </div>
    </div>
    <div class="row">
      <button class="btn primary" id="b_run" ${blocked?'disabled':''}>Run Bulk</button>
      <button class="btn ghost" id="b_stop" disabled>Stop</button>
    </div>
  </div>
  <div id="b_status" style="margin-top:16px"></div>`;
}
function hookBulk() {
  document.getElementById('b_run')?.addEventListener('click', runBulk);
  document.getElementById('b_stop')?.addEventListener('click', () => { state.streamCtl?.abort(); state.bulkActive = false; });
}
async function runBulk() {
  const cid = document.getElementById('b_client').value;
  const client = state.clients.find(x => x.id === cid);
  if (!client) return toast('Select a client', 'bad');
  const type = document.getElementById('b_type').value;
  const topic = document.getElementById('b_topic').value.trim();
  if (!topic) return toast('Theme required', 'bad');
  const qty = Math.min(10, Math.max(1, parseInt(document.getElementById('b_qty').value)||1));
  const tone = document.getElementById('b_tone').value;

  const status = document.getElementById('b_status');
  state.bulkActive = true;
  document.getElementById('b_run').disabled = true;
  document.getElementById('b_stop').disabled = false;
  const items = [];
  status.innerHTML = `<div class="card"><h3>Running: ${qty} × ${TYPES[type].label}</h3><div class="progress"><div id="bp" style="width:0%"></div></div><div id="bl" style="margin-top:10px;font-size:12px;color:var(--ink-dim)"></div></div>`;
  const log = document.getElementById('bl');
  const bar = document.getElementById('bp');

  for (let i = 0; i < qty; i++) {
    if (!state.bulkActive) break;
    if (!canGenerate(state.profile?.plan || 'trial', state.usage)) {
      log.innerHTML += `<div style="color:var(--bad)">Stopped: plan limit reached</div>`;
      break;
    }
    const angle = `Variant ${i+1} of ${qty} — distinct angle/hook from the others.`;
    const brief = {
      type, topic: `${topic} — ${angle}`,
      brand: client.name, audience: client.audience || '',
      points: `Brand voice: ${client.voice||'professional'}\nClient notes: ${client.notes||'none'}`,
      tone, length:'standard', readingLevel:'Grade 10 (general adult)',
      cta:''
    };
    log.innerHTML += `<div>→ Generating ${i+1}/${qty}…</div>`;
    let text = '';
    const ctl = new AbortController(); state.streamCtl = ctl;
    try {
      await streamGeneration({ prompt: buildPrompt(brief), type, maxTokens: 4096 }, c => { text += c; }, ctl.signal);
      state.usage += 1;
    } catch (e) {
      if (e.name === 'AbortError') break;
      log.innerHTML += `<div style="color:var(--bad)">Error on ${i+1}: ${escapeHtml(e.message)}</div>`;
      continue;
    }
    items.push({ id:newUuid(), ts:Date.now(), type, topic:`${topic} (#${i+1})`, clientId:cid, content:text, wordCount:text.trim().split(/\s+/).length, brand:client.name, tone });
    bar.style.width = Math.round(((i+1)/qty)*100) + '%';
    log.innerHTML += `<div style="color:var(--ok)">✓ Done ${i+1}/${qty} (${items[items.length-1].wordCount} words)</div>`;
  }

  if (items.length) {
    state.library.unshift(...items);
    try { for (const it of items) await db.insertJson('library', it); }
    catch (e) { toast('Some saves failed: ' + e.message, 'bad'); }
    logActivity('bulk', `Bulk: ${items.length} × ${TYPES[type].label} for ${client.name}`);
    toast(`Saved ${items.length} pieces to library`, 'ok');
  }
  state.bulkActive = false;
  updateTopbar();
  document.getElementById('b_run').disabled = !canGenerate(state.profile?.plan || 'trial', state.usage);
  document.getElementById('b_stop').disabled = true;
}

/* ============== LIBRARY ============== */
function viewLibrary() {
  const filtClient = state._libClient || '';
  const filtType = state._libType || '';
  let items = state.library;
  if (filtClient) items = items.filter(x => x.clientId === filtClient);
  if (filtType) items = items.filter(x => x.type === filtType);
  return `
  <div class="row between" style="margin-bottom:12px">
    <h2 style="margin:0">Library</h2>
    <div class="row">
      <select class="select" style="width:auto" onchange="state._libClient=this.value;window.__rerender()">
        <option value="">All clients</option>
        ${state.clients.map(c=>`<option value="${c.id}" ${filtClient===c.id?'selected':''}>${escapeHtml(c.name)}</option>`).join('')}
      </select>
      <select class="select" style="width:auto" onchange="state._libType=this.value;window.__rerender()">
        <option value="">All types</option>
        ${Object.entries(TYPES).map(([k,t])=>`<option value="${k}" ${filtType===k?'selected':''}>${t.label}</option>`).join('')}
      </select>
      ${filtClient?`<button class="btn sm" onclick="window.__exportClientBundle('${filtClient}')">Export Client PDF</button>`:''}
    </div>
  </div>
  ${items.length===0?'<div class="empty">No saved items match.</div>':
  `<div class="list">${items.map(it=>{
    const cl = state.clients.find(x=>x.id===it.clientId);
    return `<div class="item">
      <div>
        <div class="ttl">${escapeHtml(it.topic)}</div>
        <div class="meta">
          <span class="tag accent">${TYPES[it.type]?.label||it.type}</span>
          ${cl?`<span class="tag">${escapeHtml(cl.name)}</span>`:''}
          <span>${it.wordCount||0} words</span>
          <span>${fmtDate(it.ts)}</span>
        </div>
      </div>
      <div class="acts">
        <button class="btn sm" onclick="window.__viewLibItem('${it.id}')">View</button>
        <button class="btn sm" onclick="window.__copyLibItem('${it.id}')">Copy</button>
        <button class="btn sm" onclick="window.__exportTxt('${it.id}')">.txt</button>
        <button class="btn sm" onclick="window.__exportPdf('${it.id}')">PDF</button>
        <button class="btn sm danger" onclick="window.__deleteLibItem('${it.id}')">Delete</button>
      </div>
    </div>`;
  }).join('')}</div>`}`;
}
window.__rerender = render;
window.__viewLibItem = (id) => {
  const it = state.library.find(x => x.id === id); if (!it) return;
  openModal(it.topic, `<div class="output" style="min-height:auto;max-height:60vh;overflow:auto">${escapeHtml(it.content)}</div>`,
    [{ label:'Close', className:'btn ghost', action: closeModal }]);
};
window.__copyLibItem = (id) => {
  const it = state.library.find(x => x.id === id); if (!it) return;
  navigator.clipboard.writeText(it.content); toast('Copied');
};
window.__deleteLibItem = async (id) => {
  if (!confirm('Delete this item?')) return;
  state.library = state.library.filter(x => x.id !== id);
  try { await db.deleteRow('library', id); } catch (e) { return toast('Delete failed: ' + e.message, 'bad'); }
  render(); toast('Deleted');
};
window.__exportTxt = (id) => {
  const it = state.library.find(x => x.id === id); if (!it) return;
  const blob = new Blob([it.content], { type:'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'managen-' + slugify(it.topic) + '.txt';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(a.href);
};
window.__exportPdf = (id) => {
  const it = state.library.find(x => x.id === id); if (!it) return;
  const cl = state.clients.find(x => x.id === it.clientId);
  printDoc(`
    <h1 style="font-size:42px;margin:0 0 4px">${escapeHtml(it.topic)}</h1>
    <div style="font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:#888;margin-bottom:24px">
      ${TYPES[it.type]?.label||it.type} ${cl?'· '+escapeHtml(cl.name):''} · ${fmtDate(it.ts)}
    </div>
    <div style="white-space:pre-wrap;line-height:1.7;font-size:13px">${escapeHtml(it.content)}</div>
  `, 'managen-' + slugify(it.topic));
};
window.__exportClientBundle = (clientId) => {
  const cl = state.clients.find(x => x.id === clientId); if (!cl) return;
  const items = state.library.filter(x => x.clientId === clientId);
  if (!items.length) return toast('No items for this client', 'bad');
  const html = `
    <h1 style="font-size:54px;margin:0">${escapeHtml(cl.name)}</h1>
    <div style="font-size:12px;letter-spacing:.2em;text-transform:uppercase;color:#888;margin-bottom:32px">
      Content Bundle · ${items.length} pieces · ${fmtDate(Date.now())}
    </div>
    ${items.map(it=>`
      <div class="pdf-piece">
        <h2 style="font-size:32px;margin:0 0 4px">${escapeHtml(it.topic)}</h2>
        <div style="font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:#888;margin-bottom:14px">
          ${TYPES[it.type]?.label||it.type} · ${fmtDate(it.ts)}
        </div>
        <div style="white-space:pre-wrap;line-height:1.7;font-size:13px">${escapeHtml(it.content)}</div>
      </div>
    `).join('')}
  `;
  printDoc(html, 'managen-bundle-' + slugify(cl.name));
};

/* ============== OUTREACH HUB ============== */
// Replaces the previous Outreach (contact-manager) and Prospector modules with a unified
// 6-tab workspace: Discover · Write · Queue · Replies · Follow-up · Templates.

function viewOutreachHub() {
  const tab = state._hTab || 'discover';
  const tabs = [
    ['discover','Discover'],
    ['write','Write'],
    ['queue','Queue'],
    ['replies','Replies'],
    ['followup','Follow-up'],
    ['templates','Templates']
  ];
  return `
  <div class="row between" style="margin-bottom:14px">
    <h2 style="margin:0">Outreach Hub</h2>
    <div class="row">
      ${tabs.map(([k,l])=>{
        const n = hubTabBadge(k);
        return `<button class="btn sm ${tab===k?'primary':''}" onclick="state._hTab='${k}';window.__rerender()">${l}${n>0?` <span class="hub-badge">${n}</span>`:''}</button>`;
      }).join('')}
    </div>
  </div>
  ${tab==='discover'  ? renderDiscoverTab()   : ''}
  ${tab==='write'     ? renderHubWriteTab()   : ''}
  ${tab==='queue'     ? renderHubQueueTab()   : ''}
  ${tab==='replies'   ? renderRepliesTab()    : ''}
  ${tab==='followup'  ? renderFollowupTab()   : ''}
  ${tab==='templates' ? renderTemplatesTab()  : ''}`;
}
function hubTabBadge(tab) {
  if (tab === 'write')    return state.queue.filter(q => q.status === 'drafting').length;
  if (tab === 'queue')    return state.queue.filter(q => q.status === 'approved').length;
  if (tab === 'followup') return followupItems().length;
  if (tab === 'replies')  return state.queue.filter(q => ['replied','interested'].includes(q.status)).length;
  return 0;
}
function hookOutreachHub() {
  const tab = state._hTab || 'discover';
  if (tab === 'discover')  hookDiscoverTab();
  if (tab === 'write')     hookHubWriteTab();
  if (tab === 'queue')     hookHubQueueTab();
  if (tab === 'replies')   hookRepliesTab();
  if (tab === 'followup')  hookFollowupTab();
  if (tab === 'templates') hookTemplatesTab();
}

/* ----- DISCOVER TAB ----- */
function renderDiscoverTab() {
  const mode = state._hMode || 'niche';
  const platforms = state._hPlatforms || ['Google'];
  const niche = state._hNiche || NICHES[0];
  const customNiche = state._hCustomNiche || '';
  const location = state._hLocation || '';
  const keyword = state._hKeyword || '';
  const count = state._hCount || 10;
  const results = state._hResults || [];
  const selected = state._hSelected || new Set();
  const isSearching = !!state._hSearching;

  return `
  <div class="row" style="margin-bottom:12px">
    <button class="btn sm ${mode==='niche'?'primary':''}" onclick="state._hMode='niche';window.__rerender()">Niche Search</button>
    <button class="btn sm ${mode==='keyword'?'primary':''}" onclick="state._hMode='keyword';window.__rerender()">Hashtag / Keyword</button>
    <button class="btn sm" style="margin-left:auto" onclick="window.__hubAddManualProspect()">+ Add Manually</button>
  </div>
  <div class="card" style="max-width:760px">
    ${mode==='niche' ? `
      <h3>Niche Search</h3>
      <div class="field-row">
        <div class="field">
          <label>Niche</label>
          <select class="select" id="ds_niche">
            ${NICHES.map(n=>`<option ${niche===n?'selected':''}>${n}</option>`).join('')}
          </select>
        </div>
        ${niche==='Custom…'?`
        <div class="field">
          <label>Custom Niche</label>
          <input class="input" id="ds_custom" value="${escapeHtml(customNiche)}" placeholder="e.g. Yoga Studio Owner">
        </div>`:''}
        <div class="field">
          <label>Location</label>
          <input class="input" id="ds_location" value="${escapeHtml(location)}" placeholder="Los Angeles, CA — blank = online">
        </div>
        <div class="field">
          <label># Results</label>
          <select class="select" id="ds_count">
            ${[5,10,20,50].map(n=>`<option value="${n}" ${count==n?'selected':''}>${n}</option>`).join('')}
          </select>
        </div>
      </div>
    ` : `
      <h3>Hashtag / Keyword Search</h3>
      <div class="field">
        <label>Hashtag or Keyword</label>
        <input class="input" id="ds_keyword" value="${escapeHtml(keyword)}" placeholder="#medspa or &quot;real estate agent LA&quot;">
        <div style="font-size:11px;color:var(--ink-mute);margin-top:6px">Searches active accounts on the selected platforms posting about this topic.</div>
      </div>
      <div class="field">
        <label># Results</label>
        <select class="select" id="ds_count">
          ${[5,10,20,50].map(n=>`<option value="${n}" ${count==n?'selected':''}>${n}</option>`).join('')}
        </select>
      </div>
    `}
    <div class="field">
      <label>Platform Focus</label>
      <div class="row">
        ${HUB_PLATFORMS.map(p=>`
          <label style="display:flex;align-items:center;gap:6px;font-size:12px;letter-spacing:0;text-transform:none;color:var(--ink);margin:0">
            <input type="checkbox" class="ds_platform" value="${p}" ${platforms.includes(p)?'checked':''}> ${p}
          </label>
        `).join('')}
      </div>
    </div>
    <div class="row">
      <button class="btn primary" id="ds_search" ${isSearching?'disabled':''}>${isSearching?'Searching…':'Search'}</button>
      <button class="btn ghost" id="ds_stop" ${isSearching?'':'disabled'}>Stop</button>
    </div>
    <div id="ds_status" style="margin-top:10px;font-size:12px;color:var(--ink-mute)"></div>
  </div>
  ${results.length > 0 ? `
    <div class="row between" style="margin:18px 0 10px">
      <div class="row">
        <span class="pill">${results.length} results</span>
        <span class="pill">${selected.size} selected</span>
      </div>
      <button class="btn primary" id="ds_addall" ${selected.size===0?'disabled':''}>Add All Selected to Queue</button>
    </div>
    <div class="grid" style="grid-template-columns:repeat(auto-fit,minmax(340px,1fr));gap:12px">
      ${results.map((p,i)=>renderHubProspectCard(p, i, selected.has(i))).join('')}
    </div>
  ` : ''}`;
}

function renderHubProspectCard(p, idx, isSelected) {
  const indicator = p.email ? { cls:'badge r-rep',  label:'Email' }
                  : p.instagram ? { cls:'badge r-int', label:'Instagram' }
                  : p.linkedin ? { cls:'badge r-sent', label:'LinkedIn' }
                  : p.twitter ? { cls:'badge r-sent',  label:'Twitter' }
                  : p.tiktok ? { cls:'badge',           label:'TikTok' }
                  : { cls:'badge',           label:'No contact' };
  return `
    <div class="card" style="padding:14px">
      <div class="row" style="margin-bottom:8px">
        <input type="checkbox" class="ds_check" data-idx="${idx}" ${isSelected?'checked':''}>
        <div style="font-family:'Bebas Neue';font-size:18px;letter-spacing:.06em">${escapeHtml(p.business||'(unknown)')}</div>
        <span class="${indicator.cls}" style="margin-left:auto">${indicator.label}</span>
      </div>
      <div style="font-size:12px;color:var(--ink-dim);margin-bottom:8px">${p.name?escapeHtml(p.name)+' · ':''}${escapeHtml(p.location||'—')}</div>
      <div style="font-size:12px;color:var(--ink);margin-bottom:10px;font-style:italic;border-left:2px solid var(--accent);padding-left:10px">${escapeHtml(p.observation||'')}</div>
      <div style="display:flex;flex-direction:column;gap:4px;font-size:11px;color:var(--ink-dim);margin-bottom:12px">
        ${p.website?`<div>🌐 <a href="${escapeHtml(p.website)}" target="_blank" rel="noopener">${escapeHtml(p.website)}</a></div>`:''}
        ${p.instagram?`<div>📷 ${escapeHtml(p.instagram)}</div>`:''}
        ${p.linkedin?`<div>💼 <a href="${escapeHtml(p.linkedin)}" target="_blank" rel="noopener">${escapeHtml(p.linkedin)}</a></div>`:''}
        ${p.twitter?`<div>𝕏 ${escapeHtml(p.twitter)}</div>`:''}
        ${p.tiktok?`<div>♪ ${escapeHtml(p.tiktok)}</div>`:''}
        ${p.email
          ? `<div>✉️ <a href="mailto:${escapeHtml(p.email)}">${escapeHtml(p.email)}</a></div>`
          : `<div style="opacity:.6">✉️ No email found</div>`}
      </div>
      <div class="row">
        <button class="btn sm primary" onclick="window.__hubAddProspectToQueue(${idx})">Add to Queue</button>
        <button class="btn sm" onclick="window.__hubSaveProspectAsContact(${idx})">Save as Contact</button>
      </div>
    </div>`;
}

function hookDiscoverTab() {
  document.getElementById('ds_niche')?.addEventListener('change', e => { state._hNiche = e.target.value; render(); });
  document.getElementById('ds_custom')?.addEventListener('input', e => { state._hCustomNiche = e.target.value; });
  document.getElementById('ds_location')?.addEventListener('input', e => { state._hLocation = e.target.value; });
  document.getElementById('ds_keyword')?.addEventListener('input', e => { state._hKeyword = e.target.value; });
  document.getElementById('ds_count')?.addEventListener('change', e => { state._hCount = parseInt(e.target.value)||10; });
  document.querySelectorAll('.ds_platform').forEach(cb => cb.addEventListener('change', () => {
    state._hPlatforms = [...document.querySelectorAll('.ds_platform:checked')].map(c => c.value);
  }));
  document.querySelectorAll('.ds_check').forEach(cb => cb.addEventListener('change', e => {
    const idx = parseInt(e.target.dataset.idx);
    state._hSelected = state._hSelected || new Set();
    if (e.target.checked) state._hSelected.add(idx); else state._hSelected.delete(idx);
    render();
  }));
  document.getElementById('ds_search')?.addEventListener('click', runHubSearch);
  document.getElementById('ds_stop')?.addEventListener('click', () => state.streamCtl?.abort());
  document.getElementById('ds_addall')?.addEventListener('click', addAllSelectedToQueue);
}

async function runHubSearch() {
  if (!canGenerate(state.profile?.plan || 'trial', state.usage)) {
    toast('Plan limit reached — upgrade to continue', 'bad');
    return go('plans');
  }
  const mode = state._hMode || 'niche';
  const count = state._hCount || 10;
  const platforms = state._hPlatforms?.length ? state._hPlatforms : ['Google'];

  let prompt, niche, location;
  if (mode === 'niche') {
    niche = state._hNiche === 'Custom…' ? (state._hCustomNiche||'').trim() : state._hNiche;
    if (!niche) return toast('Niche required', 'bad');
    location = (state._hLocation||'').trim();
    const locationClause = location ? `in ${location}` : 'that operate online (e-commerce / DTC ok)';
    prompt = `Find ${count} real, currently-operating ${niche} businesses ${locationClause}.
Focus your search on these platforms: ${platforms.join(', ')}.

For each one return:
- business: business name
- name: owner or primary contact name (null if not findable)
- website: website URL (null if none)
- instagram: Instagram handle including @ (null if none)
- linkedin: LinkedIn URL (null if none)
- twitter: Twitter / X handle including @ (null if none)
- tiktok: TikTok handle including @ (null if none)
- email: contact email if visible in a search snippet, on their website's /contact or /about page, or in their LinkedIn bio. Prefer role-based addresses (info@, hello@, contact@) or named addresses you can actually see in a result. Return null if not visible — DO NOT GUESS.
- niche: "${niche}"
- location: "${location || 'online'}"
- observation: ONE sentence on why they likely need content help (e.g. "last blog post 8 months ago", "no Instagram presence")

Return ONLY a JSON array of objects with those exact keys. No prose, no code fences.`;
  } else {
    const kw = (state._hKeyword||'').trim();
    if (!kw) return toast('Keyword required', 'bad');
    niche = kw;
    location = '';
    prompt = `Find ${count} active accounts/businesses on ${platforms.join(', ')} actively posting about "${kw}".
Look for engaged accounts (recent posts, real audience), not bots or inactive profiles.

For each one return:
- business: business / account name
- name: owner / handle owner name (null if not findable)
- website: their linked website (null if none)
- instagram: handle including @ (null if none)
- linkedin: profile URL (null if none)
- twitter: handle including @ (null if none)
- tiktok: handle including @ (null if none)
- email: contact email if visible in a search snippet, on their linked website's /contact or /about page, or in their bio. Prefer role-based addresses (info@, hello@, contact@). Return null if not visible — DO NOT GUESS.
- niche: "${kw}"
- location: location if mentioned in bio (else "online")
- observation: ONE sentence on why they likely need content help based on their posting patterns

Return ONLY a JSON array of objects with those exact keys. No prose, no code fences.`;
  }

  state._hSearching = true;
  state._hResults = [];
  state._hSelected = new Set();
  render();
  if (document.getElementById('ds_status')) document.getElementById('ds_status').textContent = 'Querying the web…';

  let raw = '';
  const ctl = new AbortController(); state.streamCtl = ctl;
  try {
    await streamGeneration(
      { prompt, type: 'prospect_search', mode: 'prospect_search', maxTokens: 8000 },
      (chunk) => { raw += chunk; },
      ctl.signal
    );
    state.usage += 1;
  } catch (e) {
    state._hSearching = false;
    render();
    if (e.name === 'AbortError') return;
    return toast('Search failed: ' + e.message, 'bad');
  }

  const parsed = parseProspectJson(raw);
  if (!parsed || !Array.isArray(parsed)) {
    state._hSearching = false; render();
    console.error('Could not parse prospect JSON. Raw:', raw);
    return toast('Could not parse search results — try again', 'bad');
  }

  const prospects = parsed.map(p => ({
    id: newUuid(),
    status: 'found',
    ts: Date.now(),
    business: p.business || '(unknown)',
    name: p.name || null,
    website: p.website || null,
    instagram: p.instagram || null,
    linkedin: p.linkedin || null,
    twitter: p.twitter || null,
    tiktok: p.tiktok || null,
    email: p.email || null,
    niche: p.niche || niche,
    location: p.location || (location || 'online'),
    observation: p.observation || '',
    platform: detectBestPlatform(p),
    handle: p.email || p.instagram || p.linkedin || p.twitter || p.tiktok || null
  }));

  try {
    for (const p of prospects) {
      const { id, status, ts, platform, handle, email, observation, ...rest } = p;
      await db.insertProspect({ id, status, platform, handle, email, observation, data: rest });
    }
    state.prospects.unshift(...prospects);
  } catch (e) { return toast('Save failed: ' + e.message, 'bad'); }

  state._hResults = prospects;
  state._hSelected = new Set();
  state._hSearching = false;
  logActivity('prospect', `Found ${prospects.length} prospects (${niche})`);
  render();
  toast(`Found ${prospects.length} prospects`, 'ok');
  updateTopbar();
}

function detectBestPlatform(p) {
  if (p.email)     return 'cold_email';
  if (p.linkedin)  return 'linkedin';
  if (p.instagram) return 'dm';
  if (p.twitter)   return 'twitter_dm';
  if (p.tiktok)    return 'tiktok_dm';
  return 'cold_email';
}

function parseProspectJson(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch {}
  const fenced = raw.match(/```(?:json)?\s*([\s\S]+?)```/);
  if (fenced) { try { return JSON.parse(fenced[1]); } catch {} }
  const start = raw.indexOf('[');
  const end = raw.lastIndexOf(']');
  if (start !== -1 && end > start) {
    try { return JSON.parse(raw.slice(start, end + 1)); } catch {}
  }
  return null;
}

window.__hubAddManualProspect = () => {
  openModal('Add Prospect Manually', `
    <div class="field-row">
      <div class="field"><label>Business</label><input class="input" id="mp_business"></div>
      <div class="field"><label>Name</label><input class="input" id="mp_name"></div>
    </div>
    <div class="field-row">
      <div class="field"><label>Niche</label><input class="input" id="mp_niche"></div>
      <div class="field"><label>Location</label><input class="input" id="mp_location"></div>
    </div>
    <div class="field"><label>Email</label><input class="input" id="mp_email" type="email"></div>
    <div class="field-row">
      <div class="field"><label>Instagram</label><input class="input" id="mp_instagram" placeholder="@handle"></div>
      <div class="field"><label>LinkedIn URL</label><input class="input" id="mp_linkedin"></div>
    </div>
    <div class="field-row">
      <div class="field"><label>Twitter</label><input class="input" id="mp_twitter" placeholder="@handle"></div>
      <div class="field"><label>TikTok</label><input class="input" id="mp_tiktok" placeholder="@handle"></div>
    </div>
    <div class="field"><label>Website</label><input class="input" id="mp_website"></div>
    <div class="field"><label>Observation (why they need content help)</label><textarea class="textarea" id="mp_observation"></textarea></div>
  `, [
    { label:'Cancel', className:'btn ghost', action: closeModal },
    { label:'Add Prospect', className:'btn primary', action: async () => {
      const p = {
        id: newUuid(), status:'found', ts: Date.now(),
        business: document.getElementById('mp_business').value.trim(),
        name: document.getElementById('mp_name').value.trim() || null,
        niche: document.getElementById('mp_niche').value.trim(),
        location: document.getElementById('mp_location').value.trim() || 'online',
        email: document.getElementById('mp_email').value.trim() || null,
        instagram: document.getElementById('mp_instagram').value.trim() || null,
        linkedin: document.getElementById('mp_linkedin').value.trim() || null,
        twitter: document.getElementById('mp_twitter').value.trim() || null,
        tiktok: document.getElementById('mp_tiktok').value.trim() || null,
        website: document.getElementById('mp_website').value.trim() || null,
        observation: document.getElementById('mp_observation').value.trim()
      };
      if (!p.business) return toast('Business name required', 'bad');
      p.platform = detectBestPlatform(p);
      p.handle = p.email || p.instagram || p.linkedin || p.twitter || p.tiktok || null;
      try {
        const { id, status, ts, platform, handle, email, observation, ...rest } = p;
        await db.insertProspect({ id, status, platform, handle, email, observation, data: rest });
        state.prospects.unshift(p);
      } catch (e) { return toast('Save failed: ' + e.message, 'bad'); }
      logActivity('prospect', `Manually added prospect: ${p.business}`);
      closeModal(); render(); toast('Prospect added', 'ok');
    }}
  ]);
};

window.__hubAddProspectToQueue = async (idx) => {
  const p = state._hResults?.[idx]; if (!p) return;
  await addProspectToQueue(p);
};
window.__hubSaveProspectAsContact = async (idx) => {
  const p = state._hResults?.[idx]; if (!p) return;
  const platform = p.email ? 'email' : p.instagram ? 'instagram' : p.linkedin ? 'linkedin' : p.twitter ? 'twitter' : 'email';
  const handle = p.email || p.instagram || p.linkedin || p.twitter || p.tiktok || '';
  const contact = {
    id: newUuid(),
    name: p.name || p.business || 'Unknown',
    business: p.business || '',
    industry: p.niche || '',
    platform, handle,
    notes: p.observation || '',
    status: 'not_contacted',
    clientId: '',
    history: []
  };
  state.contacts.push(contact);
  try { await db.insertJson('contacts', contact); }
  catch (e) { return toast('Save failed: ' + e.message, 'bad'); }
  logActivity('contact', `Saved prospect "${contact.name}" as contact`);
  toast('Saved as contact', 'ok');
};

async function addProspectToQueue(prospect) {
  const existing = state.queue.find(q => q.prospectId === prospect.id && !['sent','skipped','converted','not_interested'].includes(q.status));
  if (existing) return toast('Already in queue', 'bad');
  const platform = prospect.platform || detectBestPlatform(prospect);
  const entry = {
    id: newUuid(),
    prospectId: prospect.id,
    status: 'drafting',
    ts: Date.now(),
    sentAt: null,
    platform,
    prospect: { ...prospect },
    message: '',
    messageType: platform,
    subject: '',
    angle: prospect.observation || ''
  };
  try {
    await db.insertQueue({
      id: entry.id, prospectId: entry.prospectId, status: entry.status,
      platform, subject: '',
      data: { prospect: entry.prospect, message: entry.message, messageType: platform, subject: entry.subject, angle: entry.angle }
    });
    await db.updateProspect(prospect.id, { status: 'queued' });
    prospect.status = 'queued';
    state.queue.unshift(entry);
  } catch (e) { return toast('Add failed: ' + e.message, 'bad'); }
  toast('Added to Write queue', 'ok');
  render();
}

async function addAllSelectedToQueue() {
  const indices = [...(state._hSelected||[])];
  if (!indices.length) return;
  for (const i of indices) {
    const p = state._hResults?.[i]; if (!p) continue;
    const existing = state.queue.find(q => q.prospectId === p.id && !['sent','skipped','converted','not_interested'].includes(q.status));
    if (existing) continue;
    await addProspectToQueue(p);
  }
  state._hSelected = new Set();
  state._hTab = 'write';
  render();
}

/* ----- WRITE TAB ----- */
function renderHubWriteTab() {
  const drafts = state.queue.filter(q => q.status === 'drafting');
  const isBatch = !!state._hBatchActive;
  const sampleId = state._hSampleId || '';
  return `
  <div class="row between" style="margin-bottom:12px">
    <div class="row">
      <span class="pill">${drafts.length} prospects awaiting message</span>
    </div>
    <div class="row">
      <select class="select" style="width:auto" id="hw_sample">
        <option value="">No free sample</option>
        ${state.library.map(it=>`<option value="${it.id}" ${sampleId===it.id?'selected':''}>Attach: ${escapeHtml(it.topic)}</option>`).join('')}
      </select>
      <button class="btn primary" id="hw_genall" ${drafts.length===0||isBatch?'disabled':''}>${isBatch?'Generating…':'Write All'}</button>
      <button class="btn ghost" id="hw_stopbatch" ${isBatch?'':'disabled'}>Stop</button>
    </div>
  </div>
  ${drafts.length===0?'<div class="empty">No prospects in the Write queue. Add some from the Discover tab.</div>':''}
  <div id="hw_batch" style="margin-bottom:12px"></div>
  <div class="list" style="gap:12px">
    ${drafts.map(d => renderHubWriteCard(d)).join('')}
  </div>`;
}
function renderHubWriteCard(d) {
  const t = HUB_MSG_TYPES[d.messageType || d.platform] || HUB_MSG_TYPES.cold_email;
  return `
  <div class="card" id="hw_${d.id}">
    <div class="row between" style="margin-bottom:8px">
      <div>
        <div style="font-family:'Bebas Neue';font-size:20px;letter-spacing:.06em">${escapeHtml(d.prospect.business||'')}</div>
        <div style="font-size:12px;color:var(--ink-dim)">${d.prospect.name?escapeHtml(d.prospect.name)+' · ':''}${escapeHtml(d.prospect.niche||'')}</div>
      </div>
      <span class="badge r-sent">${t.icon} ${t.label}</span>
    </div>
    <div style="font-size:12px;color:var(--ink);margin-bottom:10px;font-style:italic;border-left:2px solid var(--accent);padding-left:10px">${escapeHtml(d.prospect.observation||'')}</div>
    <div class="field-row">
      <div class="field">
        <label>Message Type</label>
        <select class="select hw_type" data-id="${d.id}">
          ${Object.entries(HUB_MSG_TYPES).map(([k,v])=>`<option value="${k}" ${(d.messageType||d.platform)===k?'selected':''}>${v.label}</option>`).join('')}
        </select>
      </div>
      <div class="field">
        <label>Template</label>
        <select class="select hw_template" data-id="${d.id}">
          <option value="">— None —</option>
          ${state.templates.map(t=>`<option value="${t.id}">${escapeHtml(t.name)}</option>`).join('')}
        </select>
      </div>
      <div class="field" style="grid-column:span 2">
        <label>Angle</label>
        <input class="input hw_angle" data-id="${d.id}" value="${escapeHtml(d.angle||'')}">
      </div>
    </div>
    <div class="row">
      <button class="btn primary sm hw_gen" data-id="${d.id}">Write Message</button>
      <button class="btn sm danger hw_skip" data-id="${d.id}">Skip</button>
    </div>
    <div id="hw_out_${d.id}" style="margin-top:12px;${d.message?'':'display:none'}">
      <label>Message Draft (edit inline)</label>
      ${renderPlatformPreview(d)}
      <textarea class="textarea hw_msg" data-id="${d.id}" style="min-height:160px;margin-top:8px">${escapeHtml(d.message||'')}</textarea>
      <div class="row" style="margin-top:8px">
        <button class="btn primary sm hw_approve" data-id="${d.id}">Approve</button>
        <button class="btn sm hw_regen" data-id="${d.id}">Regenerate</button>
        <button class="btn sm hw_savetpl" data-id="${d.id}">Save as Template</button>
      </div>
    </div>
  </div>`;
}
function renderPlatformPreview(d) {
  if (!d.message) return '';
  const t = HUB_MSG_TYPES[d.messageType || d.platform] || HUB_MSG_TYPES.cold_email;
  if (t.kind === 'email') {
    let subj = d.subject || '';
    let body = d.message;
    const m = body.match(/^\s*Subject:\s*(.+?)(?:\n|$)/i);
    if (m) { if (!subj) subj = m[1].trim(); body = body.slice(m[0].length).replace(/^\n+/, ''); }
    return `<div class="email-preview">
      <div class="eh">
        <div class="from">From: you@youragency.com</div>
        <div class="to">To: ${escapeHtml(d.prospect.name||d.prospect.business)} &lt;${escapeHtml(d.prospect.email||'them@example.com')}&gt;</div>
        <div class="subj">${escapeHtml(subj||'(no subject)')}</div>
      </div>
      <div class="eb">${escapeHtml(body)}</div>
    </div>`;
  }
  const cls = t.kind === 'dm' ? 'ig' : t.kind === 'linkedin' ? 'li' : t.kind === 'twitter' ? 'tw' : '';
  const init = (d.prospect.name||d.prospect.business||'?').slice(0,2).toUpperCase();
  const handle = d.prospect.instagram || d.prospect.linkedin || d.prospect.twitter || d.prospect.tiktok || '';
  return `<div class="dm-preview">
    <div class="dm-header">
      <div class="dm-avatar">${escapeHtml(init)}</div>
      <div class="dm-id">
        <div class="h">${escapeHtml(d.prospect.name||d.prospect.business||'')}</div>
        <div class="s">${t.label} · ${escapeHtml(handle)}</div>
      </div>
    </div>
    <div class="dm-bubble ${cls}">${escapeHtml(d.message)}</div>
  </div>`;
}
function hookHubWriteTab() {
  document.getElementById('hw_sample')?.addEventListener('change', e => { state._hSampleId = e.target.value; });
  document.querySelectorAll('.hw_type').forEach(s => s.addEventListener('change', async e => {
    const d = state.queue.find(x => x.id === e.target.dataset.id); if (!d) return;
    d.messageType = e.target.value;
    d.platform = e.target.value;
    await persistDraft(d);
    render();
  }));
  document.querySelectorAll('.hw_template').forEach(s => s.addEventListener('change', e => {
    const d = state.queue.find(x => x.id === e.target.dataset.id); if (!d) return;
    const tpl = state.templates.find(t => t.id === e.target.value);
    if (!tpl) return;
    d.message = (tpl.subject ? `Subject: ${tpl.subject}\n\n` : '') + tpl.body;
    persistDraft(d);
    render();
  }));
  document.querySelectorAll('.hw_angle').forEach(i => i.addEventListener('input', e => {
    const d = state.queue.find(x => x.id === e.target.dataset.id); if (d) d.angle = e.target.value;
  }));
  document.querySelectorAll('.hw_msg').forEach(t => t.addEventListener('input', e => {
    const d = state.queue.find(x => x.id === e.target.dataset.id); if (d) d.message = e.target.value;
  }));
  document.querySelectorAll('.hw_gen').forEach(b => b.addEventListener('click', e => {
    const d = state.queue.find(x => x.id === e.target.dataset.id);
    if (d) generateDraftMessage(d, false);
  }));
  document.querySelectorAll('.hw_regen').forEach(b => b.addEventListener('click', e => {
    const d = state.queue.find(x => x.id === e.target.dataset.id);
    if (d) generateDraftMessage(d, false);
  }));
  document.querySelectorAll('.hw_skip').forEach(b => b.addEventListener('click', async e => {
    const d = state.queue.find(x => x.id === e.target.dataset.id); if (!d) return;
    if (!confirm('Skip this prospect? Removes from queue.')) return;
    state.queue = state.queue.filter(x => x.id !== d.id);
    try { await db.deleteQueue(d.id); } catch (err) { return toast('Skip failed: ' + err.message, 'bad'); }
    render();
  }));
  document.querySelectorAll('.hw_approve').forEach(b => b.addEventListener('click', async e => {
    const d = state.queue.find(x => x.id === e.target.dataset.id); if (!d) return;
    if (!d.message?.trim()) return toast('Generate a message first', 'bad');
    d.status = 'approved';
    try { await db.updateQueue(d.id, { status: 'approved', data: serializeDraftData(d) }); }
    catch (err) { return toast('Approve failed: ' + err.message, 'bad'); }
    logActivity('outreach', `Approved message for ${d.prospect.business}`);
    toast('Approved → Queue tab', 'ok');
    render();
  }));
  document.querySelectorAll('.hw_savetpl').forEach(b => b.addEventListener('click', e => {
    const d = state.queue.find(x => x.id === e.target.dataset.id); if (!d) return;
    openSaveTemplateModal(d);
  }));
  document.getElementById('hw_genall')?.addEventListener('click', generateAllDrafts);
  document.getElementById('hw_stopbatch')?.addEventListener('click', () => { state._hBatchActive = false; state.streamCtl?.abort(); });
}
function serializeDraftData(d) {
  return { prospect: d.prospect, message: d.message, messageType: d.messageType || d.platform, subject: d.subject, angle: d.angle };
}
async function persistDraft(d) {
  try { await db.updateQueue(d.id, { data: serializeDraftData(d), platform: d.messageType || d.platform }); }
  catch (e) { console.warn('persist draft failed', e); }
}
function buildOutreachPrompt(d, kind = 'cold') {
  const t = HUB_MSG_TYPES[d.messageType || d.platform] || HUB_MSG_TYPES.cold_email;
  const isEmail = t.kind === 'email';
  const isLinkedIn = t.kind === 'linkedin';
  const platformLabel = t.label;
  const tone = isEmail ? 'Professional, warm, concise'
            : isLinkedIn ? 'Professional, direct, human — no jargon'
            : 'Casual, conversational, low-pressure — like a real person, not an agency';

  const sampleId = state._hSampleId;
  const sample = sampleId ? state.library.find(x => x.id === sampleId) : null;
  const sampleBlock = sample ? `\nFREE SAMPLE TO REFERENCE (a recent piece I made — mention it as a no-strings gift):\nTitle: ${sample.topic}\nType: ${TYPES[sample.type]?.label||sample.type}\nExcerpt: ${sample.content.slice(0,500)}${sample.content.length>500?'…':''}\n` : '';

  const platformConventions = isEmail
    ? 'Email — include a Subject line on the first line as "Subject: ...". Greeting, 2-3 short paragraphs, soft CTA, sign-off.'
    : isLinkedIn
      ? 'LinkedIn message — short, no subject line, professional but human. 2-3 sentences max.'
      : t.kind === 'dm'
        ? 'Instagram DM — 3-4 sentences max, casual, low-pressure, no formal sign-off.'
        : t.kind === 'twitter'
          ? 'Twitter/X DM — very short (under 240 chars), casual, almost like talking to a friend.'
          : 'TikTok DM — short, friendly, casual.';

  const lifecycle = kind === 'follow_up'
    ? 'This is a FOLLOW-UP — reference an earlier (unanswered) message gracefully without guilt-tripping. Add new value or a different angle. Keep it shorter than the first.'
    : 'This is a FIRST TOUCH — lead with the specific observation (the angle), then offer value, then a tiny ask.';

  return `You are MANAGEN's outreach specialist. Write a single, personalized ${platformLabel.toLowerCase()} to the prospect below.

PROSPECT:
- Name: ${d.prospect.name || '(unknown — open with their business name instead)'}
- Business: ${d.prospect.business || '(unknown)'}
- Niche: ${d.prospect.niche || ''}
- Location: ${d.prospect.location || 'online'}
- Website: ${d.prospect.website || '(none)'}
- Instagram: ${d.prospect.instagram || '(none)'}
- LinkedIn: ${d.prospect.linkedin || '(none)'}

YOUR ANGLE / OBSERVATION:
${d.angle || d.prospect.observation || '(find a respectful, specific reason to reach out based on their business)'}

TONE: ${tone}
PLATFORM CONVENTIONS: ${platformConventions}
${lifecycle}
${sampleBlock}
OUTPUT RULES:
- Output the message only. No preamble, no explanation, no markdown fences.
- Do NOT include placeholder brackets like [your name]. Sign as "— MANAGEN" if a sign-off is needed.
- Be specific to THIS person. Avoid generic agency-speak.
${sample?'- Reference the attached sample naturally as something you made that they can have free.':''}

Write the message now:`;
}
async function generateDraftMessage(d, partOfBatch) {
  if (!canGenerate(state.profile?.plan || 'trial', state.usage)) {
    toast('Plan limit reached — upgrade to continue', 'bad');
    if (!partOfBatch) go('plans');
    throw new Error('limit');
  }
  d.message = '';
  const outDiv = document.getElementById(`hw_out_${d.id}`);
  if (outDiv) {
    outDiv.style.display = 'block';
    const ta = outDiv.querySelector('.hw_msg'); if (ta) ta.value = '';
  }
  const prompt = buildOutreachPrompt(d, 'cold');
  const ctl = new AbortController(); state.streamCtl = ctl;
  try {
    await streamGeneration(
      { prompt, type: 'prospect_outreach', maxTokens: 1500 },
      (chunk) => {
        d.message += chunk;
        const subj = d.message.match(/^\s*Subject:\s*(.+?)(?:\n|$)/i);
        if (subj) d.subject = subj[1].trim();
        const ta = document.querySelector(`.hw_msg[data-id="${d.id}"]`);
        if (ta) ta.value = d.message;
      },
      ctl.signal
    );
    state.usage += 1; updateTopbar();
    await persistDraft(d);
  } catch (e) {
    if (e.name !== 'AbortError') { toast('Generation failed: ' + e.message, 'bad'); throw e; }
  }
}
async function generateAllDrafts() {
  const drafts = state.queue.filter(q => q.status === 'drafting' && !q.message);
  if (!drafts.length) return toast('Nothing to generate', 'bad');
  state._hBatchActive = true;
  const wrap = document.getElementById('hw_batch');
  wrap.innerHTML = `<div class="card"><h3>Generating ${drafts.length} messages</h3><div class="progress"><div id="hwbar" style="width:0%"></div></div><div id="hwlog" style="margin-top:10px;font-size:12px;color:var(--ink-dim)"></div></div>`;
  const bar = document.getElementById('hwbar');
  const log = document.getElementById('hwlog');
  let done = 0;
  for (const d of drafts) {
    if (!state._hBatchActive) break;
    log.innerHTML += `<div>→ ${escapeHtml(d.prospect.business)}…</div>`;
    try { await generateDraftMessage(d, true); }
    catch (e) {
      if (e.message === 'limit') { log.innerHTML += `<div style="color:var(--bad)">Plan limit reached</div>`; break; }
      log.innerHTML += `<div style="color:var(--bad)">Failed: ${escapeHtml(e.message||'error')}</div>`;
    }
    done++;
    bar.style.width = Math.round((done/drafts.length)*100)+'%';
  }
  state._hBatchActive = false;
  toast(`Generated ${done} messages`, 'ok');
  render();
}

function openSaveTemplateModal(d) {
  openModal('Save as Template', `
    <div class="field"><label>Template Name</label><input class="input" id="tpl_name" placeholder="e.g. Med spa cold email — local angle"></div>
    <div class="field-row">
      <div class="field"><label>Platform</label>
        <select class="select" id="tpl_platform">${Object.entries(HUB_MSG_TYPES).map(([k,v])=>`<option value="${k}" ${(d.messageType||d.platform)===k?'selected':''}>${v.label}</option>`).join('')}</select>
      </div>
      <div class="field"><label>Niche</label><input class="input" id="tpl_niche" value="${escapeHtml(d.prospect.niche||'')}"></div>
      <div class="field"><label>Type</label>
        <select class="select" id="tpl_type">${Object.entries(TEMPLATE_TYPES).map(([k,v])=>`<option value="${k}">${v}</option>`).join('')}</select>
      </div>
    </div>
  `, [
    { label:'Cancel', className:'btn ghost', action: closeModal },
    { label:'Save', className:'btn primary', action: async () => {
      const name = document.getElementById('tpl_name').value.trim();
      if (!name) return toast('Name required', 'bad');
      let body = d.message || '';
      let subject = '';
      const m = body.match(/^\s*Subject:\s*(.+?)\n+/i);
      if (m) { subject = m[1].trim(); body = body.slice(m[0].length); }
      try {
        const t = await db.insertTemplate({
          name,
          platform: document.getElementById('tpl_platform').value,
          niche: document.getElementById('tpl_niche').value.trim(),
          type: document.getElementById('tpl_type').value,
          subject, body
        });
        if (t) state.templates.unshift({ id: t.id, name: t.name, platform: t.platform, niche: t.niche, type: t.type, subject: t.subject, body: t.body, ts: new Date(t.created_at).getTime() });
      } catch (e) { return toast('Save failed: ' + e.message, 'bad'); }
      closeModal(); toast('Template saved', 'ok'); render();
    }}
  ]);
}

/* ----- QUEUE (Send) TAB ----- */
function renderHubQueueTab() {
  const items = state.queue.filter(q => q.status === 'approved');
  return `
  <div class="row between" style="margin-bottom:12px">
    <span class="pill">${items.length} ready to send</span>
    <div class="row">
      <button class="btn sm" id="hq_openall" ${items.filter(i=>HUB_MSG_TYPES[i.platform||i.messageType]?.kind==='email').length===0?'disabled':''}>Open All Emails</button>
      <button class="btn sm" id="hq_copyall" ${items.filter(i=>HUB_MSG_TYPES[i.platform||i.messageType]?.kind!=='email').length===0?'disabled':''}>Copy All DMs</button>
    </div>
  </div>
  ${items.length===0?'<div class="empty">No approved messages yet. Approve drafts in the Write tab.</div>':
  `<div class="list" style="gap:10px">${items.map(q=>renderHubQueueItem(q)).join('')}</div>`}`;
}
function renderHubQueueItem(q) {
  const t = HUB_MSG_TYPES[q.platform || q.messageType] || HUB_MSG_TYPES.cold_email;
  const expanded = state._hExpanded?.[q.id];
  const preview = (q.message||'').split('\n').filter(l=>l.trim()).slice(0,2).join(' / ');
  return `
  <div class="card" style="padding:14px">
    <div class="row between" style="margin-bottom:8px">
      <div>
        <div style="font-family:'Bebas Neue';font-size:20px;letter-spacing:.06em">${escapeHtml(q.prospect.business||'')}</div>
        <div style="font-size:12px;color:var(--ink-dim)">${q.prospect.name?escapeHtml(q.prospect.name)+' · ':''}${escapeHtml(q.prospect.niche||'')}</div>
      </div>
      <span class="badge r-int">${t.icon} ${t.label}</span>
    </div>
    ${!expanded?`<div style="font-size:12px;color:var(--ink-dim);margin-bottom:10px;line-height:1.5">${escapeHtml(preview)||'<i>(empty)</i>'}…</div>`:
    `<div style="background:#0a0b0d;border:1px solid var(--line);border-radius:var(--rad);padding:12px;font-size:13px;white-space:pre-wrap;line-height:1.6;margin-bottom:10px">${escapeHtml(q.message||'')}</div>`}
    <div class="row">
      <button class="btn sm" onclick="window.__hubExpand('${q.id}')">${expanded?'Collapse':'Expand'}</button>
      <button class="btn sm" onclick="window.__hubCopy('${q.id}')">Copy</button>
      <button class="btn primary sm" onclick="window.__hubOpen('${q.id}')">${openLabelForType(t.kind)}</button>
      <button class="btn sm" onclick="window.__hubSent('${q.id}')">Mark as Sent</button>
      <button class="btn sm danger" onclick="window.__hubDelete('${q.id}')">Delete</button>
    </div>
  </div>`;
}
function openLabelForType(kind) {
  return kind==='email' ? 'Open in Gmail'
       : kind==='dm' ? 'Open Instagram'
       : kind==='linkedin' ? 'Open LinkedIn'
       : kind==='twitter' ? 'Open Twitter / X'
       : kind==='tiktok' ? 'Open TikTok'
       : 'Open';
}
function hookHubQueueTab() {
  document.getElementById('hq_openall')?.addEventListener('click', bulkOpenEmails);
  document.getElementById('hq_copyall')?.addEventListener('click', bulkCopyDMs);
}

window.__hubExpand = (id) => {
  state._hExpanded = state._hExpanded || {};
  state._hExpanded[id] = !state._hExpanded[id];
  render();
};
window.__hubCopy = (id) => {
  const q = state.queue.find(x => x.id === id); if (!q) return;
  navigator.clipboard.writeText(q.message||''); toast('Copied');
};
window.__hubOpen = (id) => {
  const q = state.queue.find(x => x.id === id); if (!q) return;
  openMessageInPlatform(q);
};
function openMessageInPlatform(q) {
  const t = HUB_MSG_TYPES[q.platform || q.messageType] || HUB_MSG_TYPES.cold_email;
  if (t.kind === 'email') {
    if (!q.prospect.email) return toast('No email on file', 'bad');
    let subject = q.subject || '';
    let body = q.message || '';
    const m = body.match(/^\s*Subject:\s*(.+?)\n+/i);
    if (m) { if (!subject) subject = m[1].trim(); body = body.slice(m[0].length); }
    const url = `mailto:${encodeURIComponent(q.prospect.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, '_blank');
  } else if (t.kind === 'dm') {
    if (!q.prospect.instagram) return toast('No Instagram handle', 'bad');
    const handle = (q.prospect.instagram||'').replace(/^@/, '');
    window.open(`https://instagram.com/${encodeURIComponent(handle)}`, '_blank');
    navigator.clipboard.writeText(q.message||'').then(() => toast('Message copied — paste in DM', 'ok'));
  } else if (t.kind === 'linkedin') {
    if (!q.prospect.linkedin) return toast('No LinkedIn URL', 'bad');
    window.open(q.prospect.linkedin, '_blank');
    navigator.clipboard.writeText(q.message||'').then(() => toast('Message copied — paste in LinkedIn', 'ok'));
  } else if (t.kind === 'twitter') {
    if (!q.prospect.twitter) return toast('No Twitter handle', 'bad');
    const handle = (q.prospect.twitter||'').replace(/^@/, '');
    window.open(`https://twitter.com/${encodeURIComponent(handle)}`, '_blank');
    navigator.clipboard.writeText(q.message||'').then(() => toast('Message copied — paste in Twitter DM', 'ok'));
  } else if (t.kind === 'tiktok') {
    if (!q.prospect.tiktok) return toast('No TikTok handle', 'bad');
    const handle = (q.prospect.tiktok||'').replace(/^@/, '');
    window.open(`https://tiktok.com/@${encodeURIComponent(handle)}`, '_blank');
    navigator.clipboard.writeText(q.message||'').then(() => toast('Message copied — paste in TikTok DM', 'ok'));
  }
}
window.__hubSent = async (id) => {
  const q = state.queue.find(x => x.id === id); if (!q) return;
  q.status = 'sent'; q.sentAt = Date.now();
  q.followUpDue = q.sentAt + state.hubFollowupDays * 864e5;
  try {
    await db.updateQueue(q.id, { status: 'sent', sentAt: q.sentAt, followUpDue: q.followUpDue });
    if (q.prospectId) await db.updateProspect(q.prospectId, { status: 'sent' });
  } catch (e) { return toast('Update failed: ' + e.message, 'bad'); }
  logActivity('outreach', `Marked ${q.prospect.business} as sent`);
  toast('Marked as sent', 'ok');
  render();
};
window.__hubDelete = async (id) => {
  if (!confirm('Delete this message?')) return;
  state.queue = state.queue.filter(x => x.id !== id);
  try { await db.deleteQueue(id); } catch (e) { return toast('Delete failed: ' + e.message, 'bad'); }
  render();
};

async function bulkOpenEmails() {
  const items = state.queue.filter(q => q.status === 'approved' && (HUB_MSG_TYPES[q.platform||q.messageType]?.kind === 'email'));
  if (!items.length) return toast('No email-type messages in queue', 'bad');
  if (!confirm(`Open ${items.length} email windows? Your browser may block pop-ups — allow them.`)) return;
  for (const q of items) openMessageInPlatform(q);
  toast(`Opened ${items.length} compose windows`, 'ok');
}
async function bulkCopyDMs() {
  const items = state.queue.filter(q => q.status === 'approved' && (HUB_MSG_TYPES[q.platform||q.messageType]?.kind !== 'email'));
  if (!items.length) return toast('No DM-type messages in queue', 'bad');
  const text = items.map(q => {
    const t = HUB_MSG_TYPES[q.platform||q.messageType];
    const handle = q.prospect.instagram || q.prospect.linkedin || q.prospect.twitter || q.prospect.tiktok || '';
    return `=== ${q.prospect.business} (${t.label} · ${handle}) ===\n${q.message||''}`;
  }).join('\n\n---\n\n');
  await navigator.clipboard.writeText(text);
  toast(`Copied ${items.length} DMs to clipboard`, 'ok');
}

/* ----- REPLIES TAB ----- */
function renderRepliesTab() {
  const filter = state._hReplyFilter || '';
  let items = state.queue.filter(q => ['sent','replied','interested','not_interested','converted'].includes(q.status));
  if (filter) items = items.filter(q => q.status === filter);
  return `
  <div class="row between" style="margin-bottom:12px">
    <div class="row">
      <select class="select" style="width:auto" onchange="state._hReplyFilter=this.value;window.__rerender()">
        <option value="">All statuses</option>
        ${Object.entries(REPLY_STATUSES).map(([k,s])=>`<option value="${k}" ${filter===k?'selected':''}>${s.label}</option>`).join('')}
      </select>
    </div>
    <span class="pill">${items.length} ${filter?REPLY_STATUSES[filter].label.toLowerCase():'total'}</span>
  </div>
  ${items.length===0?'<div class="empty">No sent messages yet.</div>':
  `<div class="list" style="gap:10px">${items.map(q=>renderReplyItem(q)).join('')}</div>`}`;
}
function renderReplyItem(q) {
  const t = HUB_MSG_TYPES[q.platform||q.messageType] || HUB_MSG_TYPES.cold_email;
  const s = REPLY_STATUSES[q.status] || REPLY_STATUSES.sent;
  return `
  <div class="card" style="padding:14px">
    <div class="row between" style="margin-bottom:8px">
      <div>
        <div style="font-family:'Bebas Neue';font-size:18px;letter-spacing:.06em">${escapeHtml(q.prospect.business||'')}</div>
        <div style="font-size:11px;color:var(--ink-dim)">${q.prospect.name?escapeHtml(q.prospect.name)+' · ':''}${t.label} · sent ${q.sentAt?fmtDate(q.sentAt):'—'}</div>
      </div>
      <span class="badge ${s.cls}">${s.label}</span>
    </div>
    ${q.replyText?`<div style="background:#0a0b0d;border:1px solid var(--line);border-radius:var(--rad);padding:10px;margin-bottom:8px;font-size:13px;line-height:1.5"><div style="font-size:10px;letter-spacing:.2em;text-transform:uppercase;color:var(--ink-mute);margin-bottom:6px">Their reply</div>${escapeHtml(q.replyText)}</div>`:''}
    ${q.notes?`<div style="font-size:12px;color:var(--ink-dim);margin-bottom:8px"><b>Notes:</b> ${escapeHtml(q.notes)}</div>`:''}
    <div class="row">
      <button class="btn sm" onclick="window.__hubLogReply('${q.id}')">${q.replyLogged?'Edit Reply':'Log Reply'}</button>
      ${q.status==='converted' && !q.convertedClientId ? `<button class="btn primary sm" onclick="window.__hubConvertToClient('${q.id}')">Create Client Profile</button>` : ''}
      ${q.convertedClientId ? '<span class="pill ok">Client created</span>' : ''}
    </div>
  </div>`;
}
function hookRepliesTab() {}

window.__hubLogReply = (id) => {
  const q = state.queue.find(x => x.id === id); if (!q) return;
  openModal('Log Reply', `
    <div class="field">
      <label>Their reply (paste)</label>
      <textarea class="textarea" id="lr_text">${escapeHtml(q.replyText||'')}</textarea>
    </div>
    <div class="field">
      <label>Status</label>
      <select class="select" id="lr_status">
        ${Object.entries(REPLY_STATUSES).map(([k,s])=>`<option value="${k}" ${q.status===k?'selected':''}>${s.label}</option>`).join('')}
      </select>
    </div>
    <div class="field">
      <label>Notes (private)</label>
      <textarea class="textarea" id="lr_notes">${escapeHtml(q.notes||'')}</textarea>
    </div>
  `, [
    { label:'Cancel', className:'btn ghost', action: closeModal },
    { label:'Save', className:'btn primary', action: async () => {
      const status = document.getElementById('lr_status').value;
      const replyText = document.getElementById('lr_text').value.trim();
      const notes = document.getElementById('lr_notes').value.trim();
      q.status = status; q.replyText = replyText; q.notes = notes; q.replyLogged = true;
      try {
        await db.updateQueue(q.id, { status, replyText, notes, replyLogged: true });
        if (q.prospectId) await db.updateProspect(q.prospectId, { status });
      } catch (e) { return toast('Save failed: ' + e.message, 'bad'); }
      logActivity('outreach', `${q.prospect.business} → ${REPLY_STATUSES[status].label}`);
      closeModal(); render(); toast('Reply logged', 'ok');
    }}
  ]);
};
window.__hubConvertToClient = async (id) => {
  const q = state.queue.find(x => x.id === id); if (!q) return;
  const client = {
    id: newUuid(),
    name: q.prospect.business || q.prospect.name || 'New Client',
    industry: q.prospect.niche || '',
    tone: 'Professional',
    voice: '',
    audience: '',
    notes: q.prospect.observation || ''
  };
  state.clients.push(client);
  try {
    await db.insertJson('clients', client);
    q.convertedClientId = client.id;
    await db.updateQueue(q.id, { convertedClientId: client.id });
  } catch (e) { return toast('Convert failed: ' + e.message, 'bad'); }
  logActivity('client', `Converted ${q.prospect.business} → client`);
  toast('Client created — see Clients tab', 'ok');
  render();
};

/* ----- FOLLOW-UP TAB ----- */
function followupItems() {
  const days = state.hubFollowupDays || 5;
  const cutoff = Date.now() - days * 864e5;
  return state.queue.filter(q => q.status === 'sent' && !q.replyLogged && q.sentAt && q.sentAt < cutoff);
}
function renderFollowupTab() {
  const items = followupItems();
  return `
  <div class="row between" style="margin-bottom:12px">
    <div class="row">
      <span class="pill">${items.length} need follow-up</span>
    </div>
    <div class="row">
      <span style="font-size:11px;color:var(--ink-mute);letter-spacing:.18em;text-transform:uppercase">Threshold</span>
      <select class="select" style="width:auto" onchange="state.hubFollowupDays=parseInt(this.value);localStorage.setItem('managen.followupDays',this.value);window.__rerender()">
        ${FOLLOWUP_DAYS_OPTIONS.map(n=>`<option value="${n}" ${state.hubFollowupDays==n?'selected':''}>${n} days</option>`).join('')}
      </select>
    </div>
  </div>
  ${items.length===0?`<div class="empty">No follow-ups due. (Sent items with no reply after ${state.hubFollowupDays} days will appear here.)</div>`:
  `<div class="list" style="gap:10px">${items.map(q=>renderFollowupItem(q)).join('')}</div>`}`;
}
function renderFollowupItem(q) {
  const t = HUB_MSG_TYPES[q.platform||q.messageType] || HUB_MSG_TYPES.cold_email;
  const daysAgo = Math.floor((Date.now() - q.sentAt) / 864e5);
  const hasFollowup = !!state._hFollowupDrafts?.[q.id];
  const draft = state._hFollowupDrafts?.[q.id] || '';
  return `
  <div class="card" style="padding:14px">
    <div class="row between" style="margin-bottom:8px">
      <div>
        <div style="font-family:'Bebas Neue';font-size:18px;letter-spacing:.06em">${escapeHtml(q.prospect.business||'')}</div>
        <div style="font-size:11px;color:var(--ink-dim)">${t.label} · sent ${daysAgo} days ago · no reply</div>
      </div>
      <span class="badge r-int">Follow-up due</span>
    </div>
    <div style="font-size:12px;color:var(--ink-dim);margin-bottom:10px;line-height:1.5"><b>Original:</b> ${escapeHtml((q.message||'').slice(0,140))}…</div>
    ${hasFollowup?`
      <textarea class="textarea hf_msg" data-id="${q.id}" style="min-height:140px">${escapeHtml(draft)}</textarea>
      <div class="row" style="margin-top:8px">
        <button class="btn primary sm" onclick="window.__hubFollowupApprove('${q.id}')">Approve & Add to Queue</button>
        <button class="btn sm" onclick="window.__hubFollowupRegen('${q.id}')">Regenerate</button>
        <button class="btn sm" onclick="window.__hubFollowupCopy('${q.id}')">Copy</button>
      </div>
    `:`
      <div class="row">
        <button class="btn primary sm" onclick="window.__hubFollowupGen('${q.id}')">Write Follow-up</button>
        <button class="btn sm" onclick="window.__hubLogReply('${q.id}')">Log Reply Instead</button>
      </div>
    `}
  </div>`;
}
function hookFollowupTab() {
  document.querySelectorAll('.hf_msg').forEach(t => t.addEventListener('input', e => {
    state._hFollowupDrafts[e.target.dataset.id] = e.target.value;
  }));
}
window.__hubFollowupGen = async (id) => {
  const q = state.queue.find(x => x.id === id); if (!q) return;
  if (!canGenerate(state.profile?.plan || 'trial', state.usage)) {
    toast('Plan limit reached', 'bad'); return go('plans');
  }
  state._hFollowupDrafts = state._hFollowupDrafts || {};
  state._hFollowupDrafts[id] = '';
  render();
  const prompt = buildOutreachPrompt(q, 'follow_up');
  const ctl = new AbortController(); state.streamCtl = ctl;
  try {
    await streamGeneration({ prompt, type: 'prospect_outreach', maxTokens: 1200 }, (chunk) => {
      state._hFollowupDrafts[id] = (state._hFollowupDrafts[id]||'') + chunk;
      const ta = document.querySelector(`.hf_msg[data-id="${id}"]`);
      if (ta) ta.value = state._hFollowupDrafts[id];
    }, ctl.signal);
    state.usage += 1; updateTopbar();
  } catch (e) {
    if (e.name !== 'AbortError') toast('Generation failed: ' + e.message, 'bad');
  }
};
window.__hubFollowupRegen = (id) => window.__hubFollowupGen(id);
window.__hubFollowupCopy = (id) => {
  const text = state._hFollowupDrafts?.[id] || '';
  if (!text) return toast('Nothing to copy', 'bad');
  navigator.clipboard.writeText(text); toast('Copied');
};
window.__hubFollowupApprove = async (id) => {
  const text = state._hFollowupDrafts?.[id]; if (!text) return;
  const orig = state.queue.find(x => x.id === id); if (!orig) return;
  const followup = {
    id: newUuid(),
    prospectId: orig.prospectId,
    status: 'approved',
    ts: Date.now(),
    sentAt: null,
    platform: orig.platform || orig.messageType,
    prospect: orig.prospect,
    message: text,
    messageType: orig.platform || orig.messageType,
    subject: '',
    angle: 'Follow-up to earlier message'
  };
  try {
    await db.insertQueue({
      id: followup.id, prospectId: followup.prospectId, status: 'approved',
      platform: followup.platform, subject: '',
      data: { prospect: followup.prospect, message: followup.message, messageType: followup.messageType, angle: followup.angle }
    });
    state.queue.unshift(followup);
  } catch (e) { return toast('Save failed: ' + e.message, 'bad'); }
  delete state._hFollowupDrafts[id];
  logActivity('outreach', `Approved follow-up for ${orig.prospect.business}`);
  toast('Follow-up added to Queue', 'ok');
  state._hTab = 'queue';
  render();
};

/* ----- TEMPLATES TAB ----- */
function renderTemplatesTab() {
  return `
  <div class="row between" style="margin-bottom:12px">
    <span class="pill">${state.templates.length} templates</span>
    <div style="font-size:12px;color:var(--ink-dim)">Save messages from the Write tab to build this library.</div>
  </div>
  ${state.templates.length===0?'<div class="empty">No templates yet. Generate a message in the Write tab and click "Save as Template".</div>':
  `<div class="list" style="gap:10px">${state.templates.map(t=>renderTemplateCard(t)).join('')}</div>`}`;
}
function renderTemplateCard(t) {
  const platform = HUB_MSG_TYPES[t.platform];
  return `
  <div class="card" style="padding:14px">
    <div class="row between" style="margin-bottom:6px">
      <div>
        <div style="font-family:'Bebas Neue';font-size:18px;letter-spacing:.06em">${escapeHtml(t.name)}</div>
        <div style="font-size:11px;color:var(--ink-mute);letter-spacing:.18em;text-transform:uppercase">${platform?platform.label+' · ':''}${escapeHtml(TEMPLATE_TYPES[t.type]||t.type||'')}${t.niche?' · '+escapeHtml(t.niche):''}</div>
      </div>
      <button class="btn sm danger" onclick="window.__hubDeleteTemplate('${t.id}')">Delete</button>
    </div>
    ${t.subject?`<div style="font-size:13px;font-weight:500;margin-bottom:4px">Subject: ${escapeHtml(t.subject)}</div>`:''}
    <div style="font-size:13px;color:var(--ink-dim);white-space:pre-wrap;line-height:1.5">${escapeHtml(t.body)}</div>
  </div>`;
}
function hookTemplatesTab() {}
window.__hubDeleteTemplate = async (id) => {
  if (!confirm('Delete this template?')) return;
  state.templates = state.templates.filter(x => x.id !== id);
  try { await db.deleteTemplate(id); } catch (e) { return toast('Delete failed: ' + e.message, 'bad'); }
  render();
};

/* ============== INVOICES ============== */
function viewInvoices() {
  return `
  <div class="row between" style="margin-bottom:12px">
    <h2 style="margin:0">Invoices</h2>
    <button class="btn primary" id="i_new">+ New Invoice</button>
  </div>
  <div id="i_form"></div>
  <h3 style="margin-top:24px">History</h3>
  ${state.invoices.length===0?'<div class="empty">No invoices yet.</div>':
  `<div class="list">${state.invoices.map(inv=>{
    const cl = state.clients.find(x=>x.id===inv.clientId);
    return `<div class="item">
      <div>
        <div class="ttl">#${escapeHtml(inv.number)} — ${escapeHtml(cl?.name||'(no client)')}</div>
        <div class="meta">
          <span>${fmtMoney(inv.total)}</span>
          <span>Due ${escapeHtml(inv.dueDate||'—')}</span>
          <span>${fmtDate(inv.ts)}</span>
        </div>
      </div>
      <div class="acts">
        <button class="btn sm" onclick="window.__exportInvoicePdf('${inv.id}')">PDF</button>
        <button class="btn sm danger" onclick="window.__deleteInvoice('${inv.id}')">Delete</button>
      </div>
    </div>`;
  }).join('')}</div>`}`;
}
function hookInvoices() {
  document.getElementById('i_new')?.addEventListener('click', openInvoiceForm);
}
function openInvoiceForm() {
  const wrap = document.getElementById('i_form');
  const draft = { clientId:'', number:'INV-'+Date.now().toString().slice(-6), lines:[{type:'blog',qty:1,price:TYPES.blog.price}], tax:0, due:'', terms:'Net 14' };
  renderInvoiceForm();

  function renderInvoiceForm() {
    wrap.innerHTML = `
    <div class="card">
      <div class="field-row">
        <div class="field"><label>Client</label>
          <select class="select" id="iv_client">
            <option value="">— Select client —</option>
            ${state.clients.map(c=>`<option value="${c.id}" ${draft.clientId===c.id?'selected':''}>${escapeHtml(c.name)}</option>`).join('')}
          </select>
        </div>
        <div class="field"><label>Invoice Number</label><input class="input" id="iv_num" value="${escapeHtml(draft.number)}"></div>
        <div class="field"><label>Due Date</label><input class="input" id="iv_due" type="date" value="${escapeHtml(draft.due)}"></div>
        <div class="field"><label>Payment Terms</label><input class="input" id="iv_terms" value="${escapeHtml(draft.terms)}"></div>
      </div>
      <h3 style="margin-top:8px">Line Items</h3>
      <table class="invoice-table">
        <thead><tr><th>Content Type</th><th style="width:90px">Qty</th><th style="width:140px">Price each</th><th style="width:140px">Total</th><th style="width:60px"></th></tr></thead>
        <tbody>
          ${draft.lines.map((l,i)=>`<tr>
            <td><select class="select" data-i="${i}" data-k="type">${Object.entries(TYPES).map(([k,t])=>`<option value="${k}" ${k===l.type?'selected':''}>${t.label}</option>`).join('')}</select></td>
            <td><input type="number" min="1" data-i="${i}" data-k="qty" value="${l.qty}"></td>
            <td><input type="number" min="0" step="0.01" data-i="${i}" data-k="price" value="${l.price}"></td>
            <td>${fmtMoney(l.qty*l.price)}</td>
            <td><button class="btn sm danger" data-rm="${i}">×</button></td>
          </tr>`).join('')}
        </tbody>
      </table>
      <div class="row" style="margin-top:8px">
        <button class="btn sm" id="iv_add">+ Line</button>
        <div class="field" style="margin:0;margin-left:auto">
          <label style="margin-bottom:4px">Tax %</label>
          <input class="input" type="number" min="0" step="0.01" id="iv_tax" value="${draft.tax}" style="width:100px">
        </div>
      </div>
      ${(()=>{
        const sub = draft.lines.reduce((a,l)=>a + (l.qty*l.price),0);
        const tax = sub * (draft.tax/100);
        const tot = sub + tax;
        return `<div class="totals">
          <div class="lbl">Subtotal</div><div class="v">${fmtMoney(sub)}</div>
          <div class="lbl">Tax (${draft.tax}%)</div><div class="v">${fmtMoney(tax)}</div>
          <div class="lbl grand">Total</div><div class="v grand" style="font-size:28px;color:var(--accent-2)">${fmtMoney(tot)}</div>
        </div>`;
      })()}
      <div class="row" style="margin-top:14px">
        <button class="btn primary" id="iv_save">Save Invoice</button>
        <button class="btn ghost" id="iv_cancel">Cancel</button>
      </div>
    </div>`;
    wrap.querySelectorAll('[data-rm]').forEach(b => b.addEventListener('click', () => {
      draft.lines.splice(parseInt(b.dataset.rm), 1);
      if (!draft.lines.length) draft.lines.push({ type:'blog', qty:1, price:TYPES.blog.price });
      readInputs(); renderInvoiceForm();
    }));
    wrap.querySelectorAll('[data-i]').forEach(el => el.addEventListener('change', () => {
      const i = parseInt(el.dataset.i), k = el.dataset.k;
      let v = el.value;
      if (k === 'qty' || k === 'price') v = parseFloat(v) || 0;
      draft.lines[i][k] = v;
      if (k === 'type') draft.lines[i].price = TYPES[v].price;
      readInputs(); renderInvoiceForm();
    }));
    document.getElementById('iv_add').addEventListener('click', () => {
      readInputs();
      draft.lines.push({ type:'blog', qty:1, price:TYPES.blog.price });
      renderInvoiceForm();
    });
    document.getElementById('iv_cancel').addEventListener('click', () => { wrap.innerHTML = ''; });
    document.getElementById('iv_save').addEventListener('click', async () => {
      readInputs();
      if (!draft.clientId) return toast('Select a client', 'bad');
      const sub = draft.lines.reduce((a,l)=>a + (l.qty*l.price),0);
      const tax = sub * (draft.tax/100);
      const tot = sub + tax;
      const inv = { id:newUuid(), ts:Date.now(), clientId:draft.clientId, number:draft.number, lines:draft.lines.slice(), tax:draft.tax, subtotal:sub, taxAmount:tax, total:tot, dueDate:draft.due, terms:draft.terms };
      state.invoices.unshift(inv);
      try { await db.insertJson('invoices', inv); } catch (e) { return toast('Save failed: ' + e.message, 'bad'); }
      logActivity('invoice', `Invoice ${inv.number} saved (${fmtMoney(tot)})`);
      toast('Invoice saved', 'ok');
      wrap.innerHTML = ''; render();
    });
    function readInputs() {
      draft.clientId = document.getElementById('iv_client').value;
      draft.number = document.getElementById('iv_num').value;
      draft.due = document.getElementById('iv_due').value;
      draft.terms = document.getElementById('iv_terms').value;
      draft.tax = parseFloat(document.getElementById('iv_tax').value) || 0;
    }
  }
}
window.__deleteInvoice = async (id) => {
  if (!confirm('Delete this invoice?')) return;
  state.invoices = state.invoices.filter(x => x.id !== id);
  try { await db.deleteRow('invoices', id); } catch (e) { return toast('Delete failed: ' + e.message, 'bad'); }
  render();
};
window.__exportInvoicePdf = (id) => {
  const inv = state.invoices.find(x => x.id === id); if (!inv) return;
  const cl = state.clients.find(x => x.id === inv.clientId);
  const html = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px">
      <div>
        <h1 style="font-size:54px;margin:0;letter-spacing:.06em">MANAGEN</h1>
        <div style="font-size:11px;letter-spacing:.3em;text-transform:uppercase;color:#888">Content Studio Invoice</div>
      </div>
      <div style="text-align:right">
        <div style="font-family:'Bebas Neue';font-size:30px">#${escapeHtml(inv.number)}</div>
        <div style="color:#666;font-size:12px">Issued ${fmtDate(inv.ts)}</div>
        <div style="color:#666;font-size:12px">Due ${escapeHtml(inv.dueDate||'—')}</div>
        <div style="color:#666;font-size:12px">${escapeHtml(inv.terms||'')}</div>
      </div>
    </div>
    <div style="margin-bottom:24px">
      <div style="font-size:10px;letter-spacing:.3em;text-transform:uppercase;color:#888">Bill To</div>
      <div style="font-family:'Bebas Neue';font-size:28px">${escapeHtml(cl?.name||'')}</div>
      <div style="color:#555">${escapeHtml(cl?.industry||'')}</div>
    </div>
    <table class="invoice-table" style="margin-top:8px">
      <thead><tr><th>Content Type</th><th style="text-align:right">Qty</th><th style="text-align:right">Price each</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>
        ${inv.lines.map(l=>`<tr>
          <td>${TYPES[l.type]?.label||l.type}</td>
          <td style="text-align:right">${l.qty}</td>
          <td style="text-align:right">${fmtMoney(l.price)}</td>
          <td style="text-align:right">${fmtMoney(l.qty*l.price)}</td>
        </tr>`).join('')}
      </tbody>
    </table>
    <div class="totals" style="margin-top:24px;display:grid;grid-template-columns:1fr auto;gap:6px 24px;max-width:360px;margin-left:auto">
      <div class="lbl">Subtotal</div><div class="v">${fmtMoney(inv.subtotal)}</div>
      <div class="lbl">Tax (${inv.tax}%)</div><div class="v">${fmtMoney(inv.taxAmount)}</div>
      <div class="lbl grand" style="font-family:'Bebas Neue';font-size:18px">Total Due</div><div class="v grand" style="font-family:'Bebas Neue';font-size:28px;color:#ff5b1f">${fmtMoney(inv.total)}</div>
    </div>
    <div style="margin-top:48px;color:#888;font-size:11px;letter-spacing:.18em;text-transform:uppercase;border-top:1px solid #ddd;padding-top:12px">Thank you for your business.</div>
  `;
  printDoc(html, 'managen-invoice-' + slugify(inv.number));
};

/* ============== PLANS ============== */
function viewPlans() {
  const plan = state.profile?.plan || 'trial';
  return `
  <h2>Plans</h2>
  <p style="color:var(--ink-dim);max-width:640px;margin-top:-4px;margin-bottom:20px">Start free, scale when you're ready. Cancel anytime from Account → Manage Billing.</p>
  <div class="plan-cards">
    <div class="plan-card ${plan==='trial'?'current':''}">
      <div class="plan-name">Trial</div>
      <div class="plan-price">Free<span class="per"> · 5 generations</span></div>
      <ul>
        <li>5 generations total</li>
        <li>Full feature access</li>
        <li>No card required</li>
      </ul>
      <div class="plan-cta"><button class="btn ghost" disabled>Active by default</button></div>
    </div>
    <div class="plan-card ${plan==='starter'?'current':''}">
      <div class="plan-name">Starter</div>
      <div class="plan-price">$29<span class="per"> /month · 50 generations</span></div>
      <ul>
        <li>50 generations / month</li>
        <li>Unlimited clients & projects</li>
        <li>PDF + .txt export</li>
        <li>AI outreach writer</li>
      </ul>
      <div class="plan-cta">
        <button class="btn primary" id="plan_starter" ${plan==='starter'?'disabled':''}>${plan==='starter'?'Current Plan':'Subscribe'}</button>
      </div>
    </div>
    <div class="plan-card featured ${plan==='pro'?'current':''}">
      <div class="plan-name">Pro</div>
      <div class="plan-price">$59<span class="per"> /month · unlimited</span></div>
      <ul>
        <li>Unlimited generations</li>
        <li>Bulk runs (up to 10 at a time)</li>
        <li>Priority queue</li>
        <li>Everything in Starter</li>
      </ul>
      <div class="plan-cta">
        <button class="btn primary" id="plan_pro" ${plan==='pro'?'disabled':''}>${plan==='pro'?'Current Plan':'Subscribe'}</button>
      </div>
    </div>
  </div>
  ${plan!=='trial'?`<div style="margin-top:18px"><button class="btn ghost" id="plan_portal">Manage Billing</button></div>`:''}`;
}
function hookPlans() {
  document.getElementById('plan_starter')?.addEventListener('click', () => doCheckout('starter'));
  document.getElementById('plan_pro')?.addEventListener('click', () => doCheckout('pro'));
  document.getElementById('plan_portal')?.addEventListener('click', () => doPortal());
}
async function doCheckout(plan) {
  try { await startCheckout(plan); }
  catch (e) { toast('Checkout failed: ' + e.message, 'bad'); }
}
async function doPortal() {
  try { await openBillingPortal(); }
  catch (e) { toast('Portal failed: ' + e.message, 'bad'); }
}

/* ============== PRICING (rate calc) ============== */
function viewPricing() {
  return `
  <h2>Rate Calculator</h2>
  <p style="color:var(--ink-dim);margin-top:-4px;margin-bottom:20px">Estimate what to charge your own clients per piece.</p>
  <div class="card" style="max-width:680px">
    <div class="field-row">
      <div class="field"><label>Content Type</label>
        <select class="select" id="p_type">${Object.entries(TYPES).map(([k,t])=>`<option value="${k}">${t.label}</option>`).join('')}</select>
      </div>
      <div class="field"><label>Volume / month</label><input class="input" type="number" min="1" id="p_vol" value="8"></div>
      <div class="field"><label>Tier</label>
        <select class="select" id="p_tier">
          <option value="0.7">Starter (-30%)</option>
          <option value="1" selected>Mid</option>
          <option value="1.5">Premium (+50%)</option>
        </select>
      </div>
    </div>
    <div id="p_out"></div>
  </div>`;
}
function hookPricing() {
  ['p_type','p_vol','p_tier'].forEach(id => document.getElementById(id)?.addEventListener('input', calcPricing));
  calcPricing();
}
function calcPricing() {
  const t = document.getElementById('p_type').value;
  const vol = Math.max(1, parseInt(document.getElementById('p_vol').value)||1);
  const tier = parseFloat(document.getElementById('p_tier').value);
  const per = TYPES[t].price * tier;
  const monthly = per * vol;
  const yearly = monthly * 12;
  const annual3 = yearly * 3;
  const minutesPerPiece = TYPES[t].words / 250 * 12;
  const hourly = monthly / Math.max(1, (vol*minutesPerPiece/60));
  document.getElementById('p_out').innerHTML = `
    <div class="grid cards" style="margin-top:8px">
      <div class="card kpi"><div class="label">Per Piece</div><div class="value">${fmtMoney(per)}</div></div>
      <div class="card kpi"><div class="label">Monthly Retainer</div><div class="value">${fmtMoney(monthly)}</div><div class="sub">${vol} pieces / mo</div></div>
      <div class="card kpi"><div class="label">Effective Hourly</div><div class="value">${fmtMoney(hourly)}</div><div class="sub">vs. manual writing</div></div>
      <div class="card kpi"><div class="label">Annual (3 clients)</div><div class="value">${fmtMoney(annual3)}</div><div class="sub">at this tier</div></div>
    </div>`;
}

/* ============== ACCOUNT ============== */
function viewAccount() {
  const plan = state.profile?.plan || 'trial';
  const lim = planLimit(plan);
  const used = state.usage;
  const rem = remaining(plan, used);
  return `
  <h2>Account</h2>
  <div class="grid" style="grid-template-columns:1fr 1fr;gap:16px;max-width:840px">
    <div class="card">
      <h3>Current Plan</h3>
      <div class="kpi" style="padding:0">
        <div class="label">Plan</div>
        <div class="value">${(PLAN_LABELS[plan]||plan).toUpperCase()}</div>
      </div>
      <div class="row" style="margin-top:14px">
        ${plan==='pro'?'':`<button class="btn primary" onclick="window.__go('plans')">Upgrade</button>`}
        ${plan!=='trial'?`<button class="btn ghost" id="acct_portal">Manage Billing</button>`:''}
      </div>
    </div>
    <div class="card">
      <h3>Usage This Period</h3>
      <div class="kpi" style="padding:0">
        <div class="label">Generations Used</div>
        <div class="value">${used}${lim===Infinity?'':` / ${lim}`}</div>
        <div class="sub">${lim===Infinity ? 'Unlimited' : (rem === 0 ? 'Limit reached' : `${rem} remaining`)}</div>
      </div>
      ${state.profile?.current_period_end ? `<div style="margin-top:12px;font-size:11px;color:var(--ink-mute);letter-spacing:.18em;text-transform:uppercase">Resets ${escapeHtml(new Date(state.profile.current_period_end).toLocaleDateString())}</div>`:''}
    </div>
    <div class="card">
      <h3>Email</h3>
      <div style="font-size:14px;margin-bottom:14px">${escapeHtml(state.user?.email||'')}</div>
      <button class="btn ghost" onclick="window.__signOut()">Sign Out</button>
    </div>
    <div class="card">
      <h3>Danger Zone</h3>
      <p style="font-size:13px;color:var(--ink-dim);margin-top:-6px;margin-bottom:14px">Wipe all your Managen data — clients, library, invoices, contacts, activity. Cannot be undone. (Your account stays.)</p>
      <button class="btn danger ghost" id="acct_wipe">Wipe All Data</button>
    </div>
  </div>`;
}
function hookAccount() {
  document.getElementById('acct_portal')?.addEventListener('click', () => doPortal());
  document.getElementById('acct_wipe')?.addEventListener('click', async () => {
    if (!confirm('Wipe ALL Managen data (clients, library, invoices, activity, contacts)? This cannot be undone.')) return;
    try {
      await db.wipeAllUserData();
      state.clients = []; state.library = []; state.invoices = []; state.contacts = []; state.activity = [];
      render(); toast('All data wiped', 'ok');
    } catch (e) { toast('Wipe failed: ' + e.message, 'bad'); }
  });
}

/* ============== MODAL ============== */
function openModal(title, bodyHtml, buttons) {
  document.getElementById('modalTitle').textContent = title;
  document.getElementById('modalBody').innerHTML = bodyHtml;
  const f = document.getElementById('modalFoot');
  f.innerHTML = '';
  (buttons||[]).forEach(b => {
    const btn = document.createElement('button');
    btn.className = b.className || 'btn';
    btn.textContent = b.label;
    btn.addEventListener('click', b.action);
    f.appendChild(btn);
  });
  document.getElementById('modalBg').classList.add('show');
}
function closeModal() { document.getElementById('modalBg').classList.remove('show'); }
window.__closeModal = closeModal;
document.getElementById('modalBg').addEventListener('click', e => { if (e.target.id === 'modalBg') closeModal(); });

/* ============== TOAST ============== */
function toast(msg, kind) {
  const w = document.getElementById('toasts');
  const el = document.createElement('div');
  el.className = 'toast ' + (kind || '');
  el.textContent = msg;
  w.appendChild(el);
  setTimeout(() => { el.style.opacity = '0'; el.style.transition = 'opacity .25s'; }, 2200);
  setTimeout(() => el.remove(), 2600);
}

/* ============== PRINT ============== */
function printDoc(innerHtml, docName) {
  const root = document.getElementById('printRoot');
  root.innerHTML = `<div class="print-doc">${innerHtml}</div>`;
  document.getElementById('app')?.classList.add('no-print');
  const origTitle = document.title;
  if (docName) document.title = docName;
  setTimeout(() => {
    window.print();
    setTimeout(() => {
      root.innerHTML = '';
      document.getElementById('app')?.classList.remove('no-print');
      document.title = origTitle;
    }, 500);
  }, 50);
}

/* ============== DATA LOAD ============== */
async function loadAllData() {
  const [profile, clients, library, invoices, contacts, activity, prospects, queue, templates] = await Promise.all([
    db.getProfile(),
    db.listJson('clients'),
    db.listJson('library'),
    db.listJson('invoices'),
    db.listJson('contacts'),
    db.listActivity(50),
    db.listProspects(),
    db.listQueue(),
    db.listTemplates()
  ]);
  state.profile = profile;
  state.clients = clients;
  state.library = library;
  state.invoices = invoices;
  state.contacts = contacts;
  state.activity = activity;
  state.prospects = prospects;
  state.queue = queue;
  state.templates = templates;
  state.usage = profile ? await db.usageThisPeriod(profile) : 0;
}

async function refreshUsage() {
  if (!state.profile) return;
  state.usage = await db.usageThisPeriod(state.profile);
  updateTopbar();
}

/* ============== APP BOOT ============== */
function isAppPath() {
  const p = window.location.pathname;
  return p === '/app' || p.startsWith('/app/');
}

// Mount the authenticated app shell, load data, and route to the current /app view.
async function bootApp(session) {
  teardownLanding();
  state.user = session.user;
  mountAppShell();
  try {
    await loadAllData();
  } catch (e) {
    console.error('data load failed', e);
    toast('Could not load your data: ' + e.message, 'bad');
  }
  go(appViewFromPath(), { replace: true });

  window.addEventListener('popstate', () => {
    if (isAppPath()) go(appViewFromPath(), { replace: true });
    else window.location.reload(); // navigated back to landing
  });

  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') { window.location.assign('/'); }
  });
}

// Show the auth screen for the app. `mode` picks signup vs signin.
function showAppAuth(mode) {
  teardownLanding();
  // Coming from any public page (/, /privacy, /terms) → move the URL into the app.
  if (!isAppPath()) history.replaceState({}, '', '/app');
  showAuthShell(() => { window.location.assign('/app/dashboard'); }, mode);
}

/* ============== INIT ============== */
async function init() {
  // Public legal pages — available to everyone, no auth or redirect.
  const cleanPath = window.location.pathname.replace(/\/+$/, '') || '/';
  if (cleanPath === '/privacy' || cleanPath === '/terms') {
    showLegal(cleanPath === '/terms' ? 'terms' : 'privacy', (mode) => showAppAuth(mode));
    return;
  }

  const hasCheckout = new URLSearchParams(window.location.search).has('checkout');
  const session = await getSession();

  if (isAppPath()) {
    // /app and /app/* require auth.
    if (!session) {
      showAppAuth('signup');
      return;
    }
    await bootApp(session);
    // Returning from Stripe Checkout: strip the param, then give the webhook a moment.
    if (hasCheckout) {
      history.replaceState({}, '', window.location.pathname);
      setTimeout(() => loadAllData().then(render), 1500);
    }
    return;
  }

  // Root (and anything else) is public.
  if (session) {
    // Authenticated visitors skip the marketing page. Carry any Stripe return param.
    window.location.replace('/app/dashboard' + window.location.search);
    return;
  }
  showLanding((mode) => showAppAuth(mode));
}

init();
